import { Component, Injector, Input, OnInit, ViewChild, inject } from '@angular/core';
import { InfiniteScrollCustomEvent, LoadingController, ModalController, NavController, PopoverController, IonInfiniteScroll } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { AccountPlanMode } from '@omni/classes/account-management/account-plan.class';
import { Activity, ActivityType } from '@omni/classes/activity/activity.class';
import { AppointmentActivity } from '@omni/classes/activity/appointment.activity.class';
import { OrderActivity } from '@omni/classes/activity/order.activity.class';
import { OrderCaptureActivity } from '@omni/classes/activity/order-capture.activity.class';
import { PhoneActivity } from '@omni/classes/activity/phone.activity.class';
import { ProcedureTrackerActivity } from '@omni/classes/activity/procedure-tracker.activity.class';
import { StoreCheckActivity } from '@omni/classes/activity/store-check.activity.class';
import { SurgeryOrderActivity } from '@omni/classes/activity/surgery-order.activity.class';
import { FeatureActionsMap } from '@omni/classes/authentication/user.class';
import { CaseActivity } from '@omni/classes/case-intake/case-activity.class';
import { AssessmentTemplate, SurveyCategory, SurveyFrequency, SurveyStatus, TemplateType } from '@omni/classes/customer-assessment/assessment-template.class';
import { CustomerAssessment } from '@omni/classes/customer-assessment/customer-assessment.class';
import { EventActivity } from '@omni/classes/events-tool/event.class';
import { ProcedureContract } from '@omni/classes/procedure-contract/procedure-contract.class';
import { Quotes } from '@omni/classes/quotes/quotes.class';
import { IndFilterMenuModalComponent, IndFilterMenuModalDataModel } from '@omni/components/shared/ind-filter-menu-modal/ind-filter-menu-modal';
import { TimeLineItem } from '@omni/components/timeline-item/timeline-item.component';
import { AccountDataService } from '@omni/data-services/accounts/account.data.service';
import { ActivityDataService } from '@omni/data-services/activity/activity.service';
import { CaseManagementDataService } from '@omni/data-services/case-management/case-management.data.service';
import { EventsToolDataService } from '@omni/data-services/event/events-tool.data.service';
import { OrderActivityDataService } from '@omni/data-services/order-activity/order-activity.data.service';
import { ProcedureContractService } from '@omni/data-services/procedure-contract/procedure-contract.service';
import { GlanceCardViewDataModel } from '@omni/models/glanceCardViewDataModel';
import { AccountQuickActionsService } from '@omni/services/activity/account-quick-actions.service';
import { QuickActionStripService } from '@omni/services/activity/quick-action-strip.service';
import { IndDropdownListDetailModel } from '@omni/models/indDropdownListModel';
import { IndPageTitleViewDataModel } from '@omni/models/indPageTitleDataModel';
import { IndSectionHeaderViewDataModel } from '@omni/models/indSectionHeaderDataModel';
import { AccountManagementOfflineService } from '@omni/services/account-management/account-management.service';
import { AccountOfflineService } from '@omni/services/account/account.offline.service';
import { ActivityService } from '@omni/services/activity/activity.service';
import { AuthenticationService } from '@omni/services/authentication.service';
import { AccesingMode, CaseManagementService } from '@omni/services/case-management/case-management.service';
import { CustomerAssessService } from '@omni/services/customer-assess/customer-assess.service';
import { CustomerSurveyService } from '@omni/services/customer-survey/customer-survey.service';
import { DateTimeFormatsService } from '@omni/services/date-time-formats/date-time-formats.service';
import { DeviceService } from '@omni/services/device/device.service';
import { DynamicFormsService } from '@omni/services/dynamic-forms/dynamic-forms-service';
import { EventsToolService } from '@omni/services/events-tool/events-tool.service';
import { EventsService } from '@omni/services/events/events.service';
import { APP_UI_SCREEN_CLOSED } from '@omni/services/app-ui/app-ui-events';
import { FooterService, FooterViews } from '@omni/services/footer/footer.service';
import { GlobalUtilityService } from '@omni/services/global-utility.service';
import { NavigationService, PageName } from '@omni/services/navigation/navigation.service';
import { NotificationService, ToastStyle } from '@omni/services/notification/notification.service';
import { NEW_ORDER_ACTION, newOrderButton, openNewOrder } from '../account-new-order';
import { QuotesService } from '@omni/services/quote/quote-tool.service';
import { SearchConfigService } from '@omni/services/search/search-config.service';
import { Customer360UIService } from '@omni/services/ui/customer360ui.service';
import { ComponentViewMode, UIService } from '@omni/services/ui/ui.service';
import { Subject } from 'rxjs';
import {debounceTime, distinctUntilChanged, filter, first, map, takeUntil, tap} from 'rxjs/operators';
import {FieldMaterialManagementService} from "@omni/services/field-materials/field-material-management.service";
import {CustomerAsset} from "@omni/classes/field-materials/customer-asset.class";
import { OpportunityManagementService } from '@omni/services/opportunity-management/opportunity-management.service';
import { OpportunityManagementDataService } from '@omni/data-services/opportunity-management/opportunity-management.data.service';
import { MeetingStatus } from '@omni/classes/meeting/meeting.class';
import { EmailActivity, EmailStatusCodes } from '@omni/classes/activity/email.activity.class';
import { DirectMessagingService } from '@omni/services/direct-messaging/direct-messaging.service';
import { ChannelActivityType } from '@omni/classes/consent/channel.class';
import { BrandOfflineService } from '@omni/services/brand/brand.service';

import { cloneDeep, flatMap, groupBy, isEmpty, uniqBy, values } from 'lodash-es';

interface FilterOption {
  handler: (selectedItem: any, item: any, itemRef: any) => void;
  text: string | any;
  value: string;
  items: Array<{ text: any; value: string }> | any[] | any;
}
interface Product {
  id: string;
  name: string;
  quantity: number;
  quoteDetailId: string;
}
// OnPush deferred — 6+ subscriptions (accountPlans$, opportunities$, contracts$, channelList, navPop, search) set local state without markForCheck
/** The Orders status group of the timeline filter. */
const ORDER_STATUS_FILTER = 'salesOrder-status';

@Component({
  selector: 'app-account-timeline-page',
  templateUrl: 'account-timeline-page.component.html',
  styleUrls: ['account-timeline-page.component.scss'],
})
export class AccountTimelinePageComponent implements OnInit {
  private readonly injector = inject(Injector);
  // @Input() filterEnabled = true;
  @Input() searchFilterEnabled = true;
  @Input() from = 'timeline';

  @ViewChild(IonInfiniteScroll) infiniteScroll: IonInfiniteScroll;

  private accountId: string;
  private ownerId: string;
  public timelineSectionHeader: IndSectionHeaderViewDataModel;
  public timelineItems: TimeLineItem[] = [];
  public pageTitleViewData: IndPageTitleViewDataModel;
  private selectedDate: { startDate: string, endDate: string } = { startDate: '', endDate: '' };
  filterButtonBadgeCount: number = 0;
  public _allTimelineActivities: Array<any> = [];
  private _filteredTimelineActivities: Array<any> = [];
  private _filteredTimelineViewActivities: Array<TimeLineItem> = [];
  public accountTimelineFilterFormView: IndFilterMenuModalDataModel[] = [];
  public accountTimelineFilter: string = ActivityType.AllActivity;
  public filterOptions: Array<{ value: string, isSelected: boolean, displayText: string }> = [];
  isTimelineFiltered = false;
  public selectedFilters: any = [];
  userName: any;
  currentStartIndex = 30;
  public accountGlanceViewData: GlanceCardViewDataModel;
  /** The same strip the account's details card carries; pins are shared with it. */
  public accountQuickActions: any[] = [];
  public hasQuickActionOverflow = false;
  public searchPlaceholder: string;
  private formattedSurveysList: any = [];
  private filters: any = {};
  public salesOrderFilterOptions = [];
  public tempProductFilterData;
  ngUnSubscribe$ = new Subject<boolean>();
  // private quoteListFilterOptions: FilterOption[];
  // private contractListFilterOptions: FilterOption[];
  public searchInput: string = '';
  private ngDestroy$ = new Subject<boolean>();
  private searchInputSubject: Subject<string> = new Subject<string>();
  public openOpportunityDynamic: boolean = false;
  page = 1;
  pageSize = 10;
  private isActivitiesLoading: boolean = false;
  private isInitializing: boolean = false;

  constructor(
    public authenticationService: AuthenticationService,
    public accountOfflineService: AccountOfflineService,
    public accountDataService: AccountDataService,
    public device: DeviceService,
    public uiService: UIService,
    public activityService: ActivityService,
    public activityDataService: ActivityDataService,
    public navCtrl: NavController,
    public footerService: FooterService,
    private navService: NavigationService,
    public caseService: CaseManagementService,
    public caseDataService: CaseManagementDataService,
    private translate: TranslateService,
    private brandsService: BrandOfflineService,
    private directMessagingService: DirectMessagingService,
    private notifiationService: NotificationService,
    private events: EventsService,
    public eventsToolDataService: EventsToolDataService,
    public eventsToolService: EventsToolService,
    private dynamicFormService: DynamicFormsService,
    private c360Service: Customer360UIService,
    public readonly dateTimeFormatsService: DateTimeFormatsService,
    public modalCtrl: ModalController,
    private readonly utilityService: GlobalUtilityService,
    private readonly popoverCtrl: PopoverController,
    private readonly searchConfigService: SearchConfigService,
    private customerAssessService: CustomerAssessService,
    private customerSurveyService: CustomerSurveyService,
    // private quotesOfflineService: QuotesService,
    // private readonly accountManagementDataService: AccountManagementDataService,
    public accountmanagementOfflineService: AccountManagementOfflineService,
    private quotesService: QuotesService,
    private fieldMaterialManagementService: FieldMaterialManagementService,
    private orderService : OrderActivityDataService,
    private opportunityService: OpportunityManagementService,
    private loadingCtrl: LoadingController,
    private readonly opportunityDataService: OpportunityManagementDataService,
    private procedureContractService : ProcedureContractService,
    private readonly quickActions: AccountQuickActionsService,
    private readonly quickActionStrip: QuickActionStripService,
  ) {
   }

  ngOnInit() {
    // this.uiService.timelineRefreshRequired = true;
    this.openOpportunityDynamic = this.authenticationService.user.buSettings['indskr_openopportunityindynamics'];
    this.accountId = this.accountOfflineService.selected.id;
    this.ownerId = this.authenticationService.user.systemUserID;
    this._initPageTitle();
    this._initSectionHeadersView();
    this._initGlanceCardData();
    this.initList()

    // Not awaited: the strip renders in declaration order and corrects itself once the read
    // returns, rather than holding the card behind a disk hit.
    this.quickActionStrip.loadPins('account')
      .then(() => this._refreshQuickActions())
      .catch(error => console.error('Account timeline: could not read pinned quick actions', error));
    this.quickActionStrip.capChanges()
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe(() => this._refreshQuickActions());

    this.navService.childNavPopObserver.pipe(takeUntil(this.ngDestroy$)).subscribe(page => {
      if (page && this.navService.getActiveChildNavViewPageName() == PageName.AccountTimelinePageComponent && (page == PageName.ActivitiesDetailsPaneComponent || page == PageName.EmailDetailsPageComponent || page == PageName.CaseManagementDetailComponent || page == PageName.SurgeryOrderDetailsComponent || page == PageName.PhoneCallDetailsComponent || page == PageName.EventDetailsPageComponent)) {
        this._initTimelineItemsViewData();
      }
    });
    this.accountmanagementOfflineService.accountPlans$.pipe(takeUntil(this.ngDestroy$)).subscribe((data) => {
      if (data && data.length) {
        // let accountPlans = data.find(o => o.ID === this.accountService.selected.id);
        this._getTimelineActivities();
        this._initTimelineItemsViewData();
        this._applyAdvancedFilterOnAccountPlan();
      }
    });
    this.opportunityService.opportunities$.pipe(takeUntil(this.ngUnSubscribe$)).subscribe((data) => {
      if (data?.length) {
        this._getTimelineActivities();
        this._initTimelineItemsViewData();
      }
    });
    this.procedureContractService.timeLineContracts$.pipe(takeUntil(this.ngUnSubscribe$)).subscribe((res) => {
      this._getTimelineActivities();
      this._initTimelineItemsViewData();
      this._applyAdvancedFilterOnProductContract();
    });

    this.navService.childNavPopObserver.pipe(
      takeUntil(this.ngDestroy$),
      filter(page => page && this.navService.getActiveChildNavViewPageName() === PageName.AccountTimelinePageComponent),
      filter(page => [
        PageName.ActivitiesDetailsPaneComponent,
        PageName.EmailDetailsPageComponent,
        PageName.CaseManagementDetailComponent,
        PageName.SurgeryOrderDetailsComponent,
        PageName.PhoneCallDetailsComponent,
        PageName.EventDetailsPageComponent,
        PageName.AssetDetailsComponent,
      ].includes(page)),
      tap(() => {
        this._getTimelineActivities();
        this._initTimelineItemsViewData()
      })
    ).subscribe();

    // An order screen is a modal, which the nav observer never sees: what it saved shows here.
    this.events.observe(APP_UI_SCREEN_CLOSED).pipe(takeUntil(this.ngDestroy$)).subscribe(() => {
      if (this.from !== 'timeline') return;
      this.page = 1;
      this._getTimelineActivities();
      this._initTimelineItemsViewData();
    });

    this.searchInputSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.ngDestroy$)
    ).subscribe(searchText => {
      this.searchInput = searchText;
      // Reset pagination and clear existing data when searching (only for timeline view)
      if (this.from === 'timeline') {
        this.page = 1;
      }
      this._initTimelineItemsViewData(true);
    });
  }

  ngOnDestroy() {
    this.ngDestroy$.next(true);
    this.ngDestroy$.complete();
    this.ngUnSubscribe$.next(true);
    this.ngUnSubscribe$.complete();
    this.procedureContractService.isTimeLineContracts = false;
    this.selectedFilters = [];
  }

  private _initPageTitle() {
    this.pageTitleViewData = {
      id: "account-timeline-details",
      title: this.accountOfflineService?.selected?.accountName,
      controls: [{
        id: "close",
        icon: "chevron-back-outline",
        isDisabled: false,
        align: "left"
      },
      // Without the glance card (CUSTOMER360UI), New Order sits in the title bar, as on the details.
      ...(this.canCreateOrder() && !this.authenticationService.hasFeatureAction(FeatureActionsMap.CUSTOMER360UI, true)
        ? [newOrderButton(this.translate)] : [])]
    };

    switch (this.from) {
      case 'salesorder-section':
        this.searchPlaceholder = this.translate.instant('SEARCH_SALES_ORDERS');
        break;
      case 'quotes-section':
        this.searchPlaceholder = this.translate.instant('SEARCH_QUOTES');
        break;
      case 'asset-management-section':
        this.searchPlaceholder = this.translate.instant('SEARCH');
        break;
      case 'contract-section':
        this.searchPlaceholder = this.translate.instant('SEARCH_PROCEDURE_CONTRACT');
        break;
      case 'opportunities-list-section':
        this.searchPlaceholder = this.translate.instant('SEARCH_OPPORTUNITIES');
          break;
      default:
        this.searchPlaceholder = this.translate.instant('SEARCH_TIMELINE');
    }
  }

  private async _initGlanceCardData(updateButtonsOnly: boolean = false) {
    if (this.authenticationService.hasFeatureAction(FeatureActionsMap.CUSTOMER360UI, true)) {
      let glanceCardButtons = [];
      this._refreshQuickActions();
      if (this.accountOfflineService.isAffiliationEnabled) {
        glanceCardButtons.push({
          id: "affiliation-explorer",
          imgSrc: 'assets/imgs/glance-affiliation-explorer.svg',
          name: this.translate.instant('AFFILIATION_EXPLORER'),
          isDisabled: !this.accountOfflineService.hasAffiliationsForExplorer(),
          align: "right"
        });
      }
      if (this.canCreateOrder()) glanceCardButtons.push(newOrderButton(this.translate));
      if (updateButtonsOnly) {
        this.accountGlanceViewData.buttons = glanceCardButtons;
        return;
      }
      const values = await this.accountOfflineService._getQuickGlanceValues();
      if (!this.device.isOffline) {
        this.accountDataService.getEntityImageStringForAccount(this.accountOfflineService.selected).then((entityImageString => {
          if (entityImageString) {
            this.accountOfflineService.selected.entityImage = entityImageString;
            if (this.accountGlanceViewData) {
              this.accountGlanceViewData.avatarURL = this.accountOfflineService.selected.entityImage
            }
          }
        }));
      }
      const address = await this.accountOfflineService.getPrimaryAddressOfAccount(this.accountOfflineService?.selected?.id);
      this.accountGlanceViewData = {
        name: this.accountOfflineService?.selected?.accountName,
        locationText: address ? address.compositeAdd : '',
        avatarURL: this.accountOfflineService.selected.entityImage,
        thirdText: (values && values.length && values[0]) ? values[0].value : '',
        fourthText: (values && values.length && values[1]) ? values[1].value : '',
        fifthText: (values && values.length && values[2]) ? values[2].value : '',
        sixthText: (values && values.length && values[3]) ? values[3].value : '',
        thirdTextLabel: (values && values.length && values[0]) ? values[0].label : '',
        fourthTextLabel: (values && values.length && values[1]) ? values[1].label : '',
        fifthTextLabel: (values && values.length && values[2]) ? values[2].label : '',
        sixthTextLabel: (values && values.length && values[3]) ? values[3].label : '',
        buttons: glanceCardButtons,
        enableExpand: true,
        isExpanded: false,
      };
    }
  }

  private _refreshQuickActions(): void {
    // An account outside the rep's list (e.g. opened from global search) is read only.
    const types = this.accountOfflineService.getAccountById(this.accountId) ? this.quickActions.types() : [];
    ({ tiles: this.accountQuickActions, hasOverflow: this.hasQuickActionOverflow } = this.quickActionStrip.layout(types, 'account'));
  }

  public openQuickActionOverflow(event: Event): Promise<void> {
    return this.quickActionStrip.openOverflow(event, this.quickActions.types(), 'account',
      () => this._refreshQuickActions());
  }

  public async onQuickGlanceControlClick(data) {
    if (data.id) {
      if (await this.quickActionStrip.runById(this.quickActions.types(), data.id, data.event)) return;
      if (data.id === 'affiliation-explorer') this._handleAffiliationExplorer();
      if (data.id === NEW_ORDER_ACTION) void openNewOrder(this.injector, this.accountOfflineService.selected, data.event);
    }
  }

  private _handleAffiliationExplorer() {
    this.navService.pushChildNavPageWithPageTracking(PageName.AffiliationExplorerComponent, PageName.AffiliationExplorerComponent, PageName.AccountPageComponent,
      {
        from: 'AccountPageComponent', parent: this.accountOfflineService.selected,
        relatedFrom: this.accountOfflineService.linkEntityAccountFrom,
        relatedTo: this.accountOfflineService.linkEntityAccountTo,
        contactAccountAffiliation: this.accountOfflineService.linkedAccountContact,
        accountToBrand: this.accountOfflineService.brandAffiliationByAccountId
      });
  }

  /** Number in the header **/
  private _initSectionHeadersView() {
    let controls = [];
    let title = this.translate.instant('TIMELINE');
    // const excludedSections = ['accountPlan-section', 'opportunities-list-section', 'contract-section'];
    // const filteredActivitiesLength = this._filteredTimelineViewActivities.length;

    // if (this.filterEnabled && !excludedSections.includes(this.from)) {
      // controls.push({
      //   id: 'account-timeline-page-filter',
      //   text: this.getText(),
      //   isDisabled: this._allTimelineActivities.length === 0,
      //   bubbleUpEvent: true,
      //   icon: this.filterButtonBadgeCount > 0 ? '' : 'assets/imgs/customer_360_activity_type_filter.svg'
      // });
    // }

    if (this.from === 'salesorder-section') {
      title = `${this.translate.instant('ALL_ORDERS_S')}`;
    } else if (this.from === 'quotes-section') {
      title = this.translate.instant('ALL_QUOTES');
    } else if (this.from === 'asset-management-section') {
      title = `${this.translate.instant('ALL_MY_ASSETS')}`;
    } else if (this.from === 'contract-section') {
      title = `${this.translate.instant('ALL_PROCEDURE_CONTRACTS')}`;
    } else if (this.from === 'accountPlan-section') {
      title = `${this.translate.instant('ALL_ACCOUNT_PLANS')}`;
    } else if (this.from === 'opportunities-list-section') {
      title = `${this.translate.instant('ALL_OPPORTUNITIES')}`;
    }

    this.timelineSectionHeader = {
      id: 'account-timeline-section',
      title,
      controls
    };
  }

  public async onPageTitleControlClick(id: string) {
    switch (id) {
      case 'close':
        this._closePage();
        break;
      case NEW_ORDER_ACTION:
        void openNewOrder(this.injector, this.accountOfflineService.selected);
    }
  }

  /** As on the account's details: wherever its activity tiles are. */
  private canCreateOrder(): boolean {
    return this.authenticationService.hasFeatureAction(FeatureActionsMap.NEW_ORDER_FLOW)
      && !!this.accountOfflineService.getAccountById(this.accountId) && !this.isNavigationBlocked();
  }

  private _closePage() {
    this.navService.popChildNavPageWithPageTracking();
  }

  // async ngDoCheck() {
  //   if (this.uiService.timelineRefreshRequired) {
  //     this.uiService.timelineRefreshRequired = false;
  //     try {
  //       this.uiService.displayLoader({}, true);
  //       await this.accountDataService.getAccountTimelineInfo(this.accountOfflineService.selected).then(() => {
  //         this.initList();
  //         this.uiService.dismissLoader();
  //       });
  //     } catch (error) {
  //       console.log("Error getting account activities for timeline", error);
  //       this.uiService.dismissLoader();
  //     }
  //   }
  // }

  getProductForFilterPopover(orders : OrderActivity[]) {
    let products = flatMap(orders, (order) => {
      return order.products.length ? order.products.map((prod) => ({ text: prod.productName, value: prod.productId })) : [];
    });

    this.tempProductFilterData = uniqBy(products, 'text')
    return this.tempProductFilterData;
  }

  getQuotesForFilterPopover(quotes) {
    let products = flatMap(quotes, (quote) => {
      return quote.products.length ? quote.products.map((prod) => ({ text: prod.name, value: prod.id })) : [];
    });
    this.tempProductFilterData = uniqBy(products, 'text')
    return uniqBy(products, 'text');
  }

  getContractsForFilterPopover(procedureContracts : ProcedureContract[]) {
    let products = [];
    for (let proceduerContract of procedureContracts) {
      if (proceduerContract.productName && proceduerContract.indskr_product_value) {
        products.push({ text: proceduerContract.productName , value: proceduerContract.indskr_product_value })
      }
    }

    this.tempProductFilterData = uniqBy(products, 'text')
    return uniqBy(products, 'text');
  }

  // Filter Quotes
  filterOrders() {
    this._filteredTimelineActivities = this._allTimelineActivities.filter((order: OrderActivity) => {
      let statusMatch = this.filters.statusString ? order.orderStatusString === this.filters.statusString : true;
      let productMatch = this.filters.products ? order.products.some(prod => prod.productId === this.filters.products) : true
      return statusMatch && productMatch;
    });

    if(this.filters.statusString){
      this.accountTimelineFilter = this.filters.statusString;
    }
    this._initTimelineItemsViewData();
  }

  filterQuotes() {
    this._filteredTimelineActivities = this._allTimelineActivities.filter((quote: Quotes) => {
      let statusMatch = this.filters.statusFilter ? quote.statuscode === this.filters.statusFilter : true;
      let productMatch = this.filters.productFilter ? quote.products.some(prod => prod.id === this.filters.productFilter) : true
      return statusMatch && productMatch;
    });

    if(this.filters.statusString){
      this.accountTimelineFilter = this.filters.statusString;
    }
    // this.initMyQuotesHeaderModel();
    this.getFilterButtonBadgeCount();
    this._initTimelineItemsViewData()
  }

  filterProcedureContracts() {
    this._filteredTimelineActivities = this._allTimelineActivities.filter((proceduerContract: ProcedureContract) => {
      let statusMatch = this.filters.statusFilter ? proceduerContract.statusString === this.filters.statusFilter.text : true;
      let productMatch = this.filters.productFilter ? proceduerContract.indskr_product_value === this.filters.productFilter.value : true
      let contractTypeMatch = this.filters.contractTypeFilter ? proceduerContract.indskr_contracttypes === this.filters.contractTypeFilter.value : true
      return statusMatch && productMatch && contractTypeMatch;
    });

    if(this.filters.statusString){
      this.accountTimelineFilter = this.filters.statusString;
    }


    // this.initMyQuotesHeaderModel();
    this.getFilterButtonBadgeCount();
    this._initTimelineItemsViewData()
  }

  getFilterButtonBadgeCount() {
    return Object.keys(this.filters).length;
  }

  private _getTimelineItemViewForActivities(activities: Array<any>): Array<TimeLineItem> {
    const itemsView: Array<TimeLineItem> = [];

    if (!activities || activities.length === 0) {
      return itemsView;
    }

    activities.forEach(item => {
      let activity = null;

      if (item.type === ActivityType.Event) {
        activity = this.eventsToolService.getEventDetailsbyId(item.ID);
        if (activity) {
          activity.type = ActivityType.Event;
          activity.scheduledStart = this.dateTimeFormatsService.isValidDate(activity.scheduledStart) ? activity.scheduledStart : item.scheduledStart;
        }
      }

      const view = this.c360Service.getViewForJourneyItem(false, activity || item);
      if (view) {
        if (item.type === ActivityType.Event && !activity) {
          view.disableEndArrow = true;
        }
        itemsView.push(view);
      }
    });

    return itemsView;
  }

  public doInfinite(event) {
    // Only apply old infinite scroll logic for non-timeline views
    if (this.from === 'timeline') {
      event.target.complete();
      return;
    }
    
    this.timelineItems.push(...this.sliceActivities(this.currentStartIndex + 1, this.currentStartIndex + 30));
    this.currentStartIndex += 30;
    event.target.complete();
    this.getFilterButtonBadgeCount();
    this._initTimelineItemsViewData();
  }

  private sliceActivities(startIndex: number, count: number) {
    return this._filteredTimelineViewActivities.length < count ? this._filteredTimelineViewActivities.slice(startIndex) : this._filteredTimelineViewActivities.slice(startIndex, count);
  }

  /**
   * Filters and sorts timeline items by scheduledStart date (newest first)
   * @param items Array of timeline items to filter and sort
   * @param isViewItem Whether the items are TimeLineItem objects (true) or raw activity objects (false)
   * @returns Filtered and sorted array
   */
  private filterAndSortTimelineItems<T>(items: T[], isViewItem: boolean = false): T[] {
    if (!items || items.length === 0) {
      return items;
    }

    if (isViewItem) {
      // For TimeLineItem objects, access scheduledStart through refObject
      return items.filter((item: any) => item.refObject && item.refObject.scheduledStart)
        .sort((a: any, b: any) => new Date(b.refObject.scheduledStart).getTime() - new Date(a.refObject.scheduledStart).getTime());
    } else {
      // For raw activity objects, access scheduledStart directly
      return items.filter((item: any) => item.scheduledStart)
        .sort((a: any, b: any) => new Date(b.scheduledStart).getTime() - new Date(a.scheduledStart).getTime());
    }
  }

  // Card structure
  private async _initTimelineItemsViewData(clearExisting: boolean = true) {
    // For timeline view, use new infinite scroll logic
    if (this.from === 'timeline') {
      if (clearExisting) {
        this._filteredTimelineViewActivities = [];
        this.timelineItems = [];
        this.page = 1; // Reset pagination when clearing
      }
      
      if (this.accountId) {
        let newViewActivities: TimeLineItem[] = [];
        newViewActivities = this._getTimelineItemViewForActivities(this._filteredTimelineActivities);

        // Add new activities to existing ones if not clearing
        if (clearExisting) {
          this._filteredTimelineViewActivities = newViewActivities;
        } else {
          this._filteredTimelineViewActivities.push(...newViewActivities);
        }

        // Search Logic
        this._filteredTimelineViewActivities = this._filterTimelineItemsBySearch(this._filteredTimelineViewActivities);
        
        // Sort to maintain chronological order
        this._filteredTimelineViewActivities = this.filterAndSortTimelineItems(this._filteredTimelineViewActivities, true);
        
        // Update timelineItems with all filtered activities
        this.timelineItems = [...this._filteredTimelineViewActivities];
        this._initSectionHeadersView();
      }
    } else {
      // Original logic for all other sections (quotes-section, salesorder-section, etc.)
      this._filteredTimelineViewActivities = [];
      if (this.accountId) {
        if (this.from === 'quotes-section') {
          this._filteredTimelineViewActivities = this._filteredTimelineActivities.map((quote) => this.c360Service.getTimelineItemViewForQuotes(quote));
        } else if (this.from === 'salesorder-section') {
          this._filteredTimelineViewActivities = this._getTimelineItemViewForActivities(this._filteredTimelineActivities);
        } else if (this.from === 'asset-management-section') {
          this._filteredTimelineViewActivities = this._filteredTimelineActivities.map(this.c360Service.assetManagementTimeline());
        } else if (this.from === 'accountPlan-section') {
          this._filteredTimelineViewActivities = this._filteredTimelineActivities.map((accountPlan) => this.c360Service._getProfileItemViewForAccountPlan(accountPlan))
        } else if (this.from === 'contract-section') {
          this._filteredTimelineViewActivities = this._filteredTimelineActivities.map((procedureContract) => this.c360Service.getTimelineItemViewForContract(procedureContract));
        } else if (this.from === 'opportunities-list-section') {
          if (this._filteredTimelineActivities && this._filteredTimelineActivities.length > 0) {
            for (let item of this._filteredTimelineActivities) {
              let view = await this.c360Service._getProfileItemViewForOpportunities(item);
              if (view) {
                if(this.openOpportunityDynamic){
                  view.popupImage = true;
                }
                this._filteredTimelineViewActivities.push(view);
              }
            }
          } else {
            this._filteredTimelineViewActivities = [];
          }
        } else {
          this._filteredTimelineViewActivities = this._getTimelineItemViewForActivities(this._filteredTimelineActivities);
        }

        // Search Logic
        this._filteredTimelineViewActivities = this._filterTimelineItemsBySearch(this._filteredTimelineViewActivities);
        
        // Sort to maintain chronological order
        this._filteredTimelineViewActivities = this.filterAndSortTimelineItems(this._filteredTimelineViewActivities, true);
        
        this.timelineItems = this._filteredTimelineViewActivities.slice(0, this.currentStartIndex);
        this._initSectionHeadersView();
      }
    }
  }

  handleSearch() {
    this.searchInputSubject.next(this.searchInput);
  }

  public onTimelineTypeChange(activityType) {
    if (!this._allTimelineActivities) return;
    
    // Reset pagination when changing timeline type (only for timeline view)
    if (this.from === 'timeline') {
      this.page = 1;
    }
    
    // Data-driven activity type filter map
    const activityFilterMap = new Map<string, (activity: any) => boolean>([
      [ActivityType.MyActivity, (item) => item.ownerId == this.ownerId],
      [ActivityType.Appointment, (activity) => activity.type == ActivityType.Appointment && activity.location != 'LiveMeet'],
      [ActivityType.LiveMeet, (activity) => activity.type == ActivityType.Appointment && activity.location === 'LiveMeet'],
      [ActivityType.CaseIntake, (activity) => activity.type == ActivityType.CaseIntake],
      [ActivityType.Order, (activity) => activity.type == ActivityType.Order],
      [ActivityType.PhoneCall, (activity) => activity.type == ActivityType.PhoneCall],
      [ActivityType.SurgeryOrder, (activity) => activity.type == ActivityType.SurgeryOrder],
      [ActivityType.Event, (activity) => activity.type == ActivityType.Event],
      [ActivityType.StoreCheck, (activity) => activity.type == ActivityType.StoreCheck],
      [ActivityType.CustomerSurvey, (activity) => activity.type == ActivityType.CustomerSurvey],
    ]);

    const filterFn = activityFilterMap.get(activityType);
    this._filteredTimelineActivities = filterFn
      ? this._allTimelineActivities.filter(filterFn)
      : this._allTimelineActivities;
    this._initTimelineItemsViewData(true);
  }

  async _getTimelineActivities() {
    if (this.isInitializing) return;
    this.isInitializing = true;
    
    try {
      let items: any = [];
      switch (this.from) {
        case 'quotes-section':
          this.quotesService.quotesList$.pipe(
            takeUntil(this.ngDestroy$),
            map(quotes => quotes.filter(quote => quote.accountId === this.accountId)),
            tap(filteredQuotes => {
              items = filteredQuotes;
              this._updateTimelineActivities(items);
              if (this.searchFilterEnabled) {
                this._applyAdvancedFilterOnQuotesSection();
              }
            })
          ).subscribe();
          break;
        case 'salesorder-section':
          items = this.activityService.activities
            .filter(order => order instanceof OrderActivity && order.type === ActivityType.Order && !order.isHardDeleted && order.accountId == this.accountId)
            .sort((a: any, b: any) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()) as Array<OrderActivity>;
          this._updateTimelineActivities(items);
          if (this.searchFilterEnabled) {
            this._applyAdvancedFilterOnSalesOrders();
          }
          break;
        case 'asset-management-section':
          items = this.fieldMaterialManagementService.customerAssets
            .filter(asset => asset.msdyn_account === this.accountId)
            .sort((a, b) => new Date(b.createdon).getTime() - new Date(a.createdon).getTime());
          this._updateTimelineActivities(items)
          if (this.searchFilterEnabled) {
            this._applyAdvancedFilterOnAssetManagement();
          }
          break;
        case 'accountPlan-section':
          items = this.accountmanagementOfflineService.accountPlans.filter((accountPlan) => accountPlan.accountId === this.accountId).sort((a, b) => {
            if (a.startDate == "" && b.startDate == "") return 0;
            if (a.startDate == "") return 1
            if (b.startDate == "") return -1
            return parseInt(b.startDate) - parseInt(a.startDate)
          });
           this._updateTimelineActivities(items);
          if (this.searchFilterEnabled) {
            this.initFilterFormViewsForAccountPlan();
          }
            break;
        case 'contract-section':
          try {
            items = this.procedureContractService.getTimelineProcedureContracts();
            this._updateTimelineActivities(items);
            this._initFilterFormViewsForProcedureContract();
          } catch (err) {
            console.error('Failed to fetch procedure contract');
          }
          break;
        case 'opportunities-list-section':
          let loader = await this.loadingCtrl.create();
          items = this.opportunityService.opportunities.filter((oppo) => oppo.accountID === this.accountId).sort((a, b) => {
            if (a.createdOn && b.createdOn) {
              return parseInt(b.createdOn) - parseInt(a.createdOn)
            } else {
              return 0;
            }
          });
          await loader.present();
          try {
            for (let item of items) {
              let res = await this.opportunityDataService.getOpportunityActiveStageIdByProcessInstanceId(item.opportunityBusinessProcessInstanceId, item.opportunityBusinessProcessSchemaName);
              if (res && Array.isArray(res)) {
                if (res[0] && res[0]['_activestageid_value']) {
                  item.opportunityStage = res[0]['_activestageid_value']
                  item.opportunityStageValue = res[0]['_activestageid_value@OData.Community.Display.V1.FormattedValue']
                }
              }
            }
            this._updateTimelineActivities(items);
            this._initFilterFormViewsForOpportunities()
            await loader.dismiss();
          } catch (err) {
            await loader.dismiss();
          }
          break;
        default:
          items = await this.combineEventsAndActivities(items);
          if (this.authenticationService.hasFeatureAction(FeatureActionsMap.ACCOUNT_SURVEY)) {
            items = this.combineSurveysAndActivities(items);
          }
          items = this.filterAndSortTimelineItems(items, false);
          this._updateTimelineActivities(items);
          break;
      }
    } finally {
      this._initFilterFormViews();
      this.isInitializing = false;
    }
  }

  private _updateTimelineActivities(items: any) {
    this._filteredTimelineActivities = this._allTimelineActivities = items;
    // Reset pagination when updating timeline activities (only for timeline view)
    if (this.from === 'timeline') {
      this.page = 1;
    }
    this.onTimelineTypeChange(this.accountTimelineFilter || ActivityType.AllActivity);
  }

  private async combineEventsAndActivities(items: any) {
      const [hasData, data] = await this.loadActivitiesByPage(this.page);
    return data;
  }

  private combineSurveysAndActivities(items: any) {
    this.formattedSurveysList = [];
    let surveysByAccountId: any = this.customerAssessService.accountSurveysForTimeline.filter(survey => survey.surveyStatus != SurveyStatus.SAVED) || [];
    if (!isEmpty(surveysByAccountId)) {
      let groupedResponsesByDate: any = [];
      groupedResponsesByDate = groupBy(surveysByAccountId, 'indskr_assessmentdate');
      groupedResponsesByDate = values(groupedResponsesByDate);

      groupedResponsesByDate.forEach((resp) => {
        let activity: any = {};

        const templateId: string = resp[0]['_indskr_template_value'];
        const foundTemplate: AssessmentTemplate = this.customerAssessService.surveytemplates.find(survey => survey.indskr_assessmenttemplateid == templateId);

        let surveyFrequency: string = foundTemplate['indskr_surveyfrequency'] ? foundTemplate['indskr_surveyfrequency'] == SurveyFrequency.ONCE ? this.translate.instant("ONE_TIME") : this.translate.instant("RECURRING") : '';
        activity['type'] = ActivityType.CustomerSurvey;
        activity['subject'] = resp[0]['indskr_name'] || '';
        activity['assessmentId'] = resp[0]['indskr_customerassessmentid'];
        activity['scheduledStart'] = resp[0]['indskr_assessmentdate'];
        activity['surveyFrequency'] = surveyFrequency;
        activity['surveyedBy'] = resp[0]['_modifiedby_value_Formatted'] || '';
        activity['templateId'] = templateId;
        activity['category'] = SurveyCategory.ACCOUNT;
        // activity['rawData'] = resp;
        items.push(activity);
        this.formattedSurveysList.push(activity);
      });
    }
    return items;
  }

  public onFilterClick(ev) {
    if (this._allTimelineActivities.length == 0) return;
    this.openTimelineFilter(ev);
  }

  /** Open Details Page **/
  openActivityDetails = async (ev) => {
    const selected = this.timelineItems.find(a => ev && a.id == ev.value)?.refObject;
    // A new order flow order opens in the order screen, under the same guards as any other item.
    if (selected instanceof OrderCaptureActivity) {
      if (!this.isNavigationBlocked()) await this.openOrderCapture(selected);
    } else await this.openListedActivity(ev);
  }

  private readonly openListedActivity = async (ev) => {
    let item: any = this.timelineItems.find(a => ev && a.id == ev.value);
    if (item) {
      let selectedItem = item.refObject;

      if (this.from === 'quotes-section') {
        this.openQuotesDetailsPage(selectedItem);
        return;
      } else if (this.from === 'salesorder-section') {
        this.displayActivityDetails(selectedItem as Activity, true);
        return;
      } else if (this.from === 'asset-management-section') {
        this.openAssetManagementPage(selectedItem);
        return;
      } else if (this.from === 'accountPlan-section') {
        this.openAccountPlanDetailsPage(item.refObject)
        return;
      } else if (this.from === 'contract-section') {
        this.openProcedureContactPage(item);
        return;
      } else if (this.from === 'opportunities-list-section') {
        this.openOpportuniyDetailsPage(item.refObject)
      }


      //prevent navigation issue
      if (this.isNavigationBlocked()) {
        return;
      }

      if (this.device.isOffline) {
        this.notifiationService.notify(this.translate.instant('ACCOUNT_TIMELINE_NO_INTERNET'), 'Account Timeline', 'top', ToastStyle.DANGER);
        return;
      }

      this.uiService.showNewActivity = false;
      this.uiService.activeView = 'ActivitySkeleton';
      setTimeout(() => {
        this.displayActivityDetails(selectedItem as Activity);
      }, 0);
    }
  }

  openAssetManagementPage(asset: CustomerAsset) {
    this.fieldMaterialManagementService.selectedAsset = asset;
    this.navService.pushChildNavPageWithPageTracking(
      PageName.AssetDetailsComponent,
      PageName.AssetDetailsComponent,
      PageName.AccountPageComponent,
      {
        selectedAsset: asset,
        nestedRoute: true
      }
    );
  }

  openQuotesDetailsPage(quote: Quotes) {
    this.navService.pushChildNavPageWithPageTracking(PageName.QuoteDetailsComponent,
      PageName.QuoteDetailsComponent,
      PageName.AccountPageComponent, {
        selectedQuote: quote,
        nestedRoute: true,
      });
  }

  openAccountPlanDetailsPage(item) {
    let view =this.accountmanagementOfflineService.selectedMode = AccountPlanMode.VIEW
    this.navService.pushChildNavPageWithPageTracking(PageName.AccountPlanDetailsComponent, PageName.AccountPlanDetailsComponent, PageName.AccountPageComponent,{
      data:item,
      selectedMode:view,
      from: 'AccountTimeline'
    });
      this.accountmanagementOfflineService.selectedAccountPlan$.next(item);
  }

  openProcedureContactPage(item) {
    this.procedureContractService.isProcedureContractCreationActive = false;
    this.navService.pushChildNavPageWithPageTracking(PageName.SurgeryOrderContractDetailsComponent,
      PageName.SurgeryOrderContractDetailsComponent,
      PageName.AccountPageComponent,
      {
        procedureContract: item.refObject,
        viewMode: ComponentViewMode.READONLY
      });
  }

  openOpportuniyDetailsPage(item) {
    if (item) {
      let opportunityId = item.ID
      this.navService.pushChildNavPageWithPageTracking(PageName.OpportunityDetailsComponent,
        PageName.OpportunityDetailsComponent, PageName.AccountPageComponent, { from: 'AccountTimeline', opportunity: this.opportunityService.getOpportunityDetailsbyId(opportunityId) })
    }
  }

  /** Read-only, preview, affiliation and in-meeting views open nothing from the timeline. */
  private isNavigationBlocked(): boolean {
    return (this.accountOfflineService.accessedAccountListFrom == PageName.ContactDetailsComponent && this.accountOfflineService.accountPageMode === ComponentViewMode.READONLY)
      || this.accountOfflineService.accountPageMode === ComponentViewMode.PREVIEW
      || this.dynamicFormService.isOpenedAffiliatedContactOnAccount
      || this.dynamicFormService.isOpenedAffiliatedAccountOnAccount
      || this.caseService.accessedFrom == AccesingMode.INMEETING;
  }

  /** Statuses a further page brings join the Orders status filter; what is chosen stays chosen. */
  private refreshOrderCaptureStatusOptions(): void {
    const view = this.accountTimelineFilterFormView.find(form => form.id === ORDER_STATUS_FILTER);
    if (!view || !this.authenticationService.hasFeatureAction(FeatureActionsMap.NEW_ORDER_FLOW)) return;
    const chosen = new Set(view.options.filter(option => option.isSelected).map(option => option.id));
    view.options = this.orderCaptureStatusOptions().map(option => ({ ...option, isSelected: chosen.has(option.id) }));
  }

  /** One status option per status among the orders listed. */
  private orderCaptureStatusOptions() {
    const labels = new Set<string>();
    for (const activity of this._allTimelineActivities ?? [])
      if (activity instanceof OrderCaptureActivity && activity.statusLabel) labels.add(activity.statusLabel);
    return [...labels].sort().map(label => ({
      group: this.translate.instant('STATUS'),
      id: ActivityType.Order + '_' + label,
      displayName: label,
      isSelected: false,
    }));
  }

  /** To edit when it is the rep's open draft, else read-only with Dynamics' figures (OMNI-60747). */
  private async openOrderCapture(order: OrderCaptureActivity): Promise<void> {
    try {
      const opener = await import('@omni/services/order-capture/order-capture-opener.service').then(m => m.OrderCaptureOpener);
      await this.injector.get(opener).open(this.accountOfflineService.selected, order.ID, order.isOpen);
    } catch (error) {
      console.error('Account timeline: the order could not be opened', error);
      this.notifiationService.notify(this.translate.instant('APP_UI_LOAD_FAILED'), 'Order Capture', 'top', ToastStyle.DANGER);
    }
  }

  async displayActivityDetails(selActivity, overrideOfflineCheck = false) {
    if (this.device.isOffline && !overrideOfflineCheck) {
      this.notifiationService.notify(this.translate.instant('ACCOUNT_TIMELINE_NO_INTERNET'), 'Account Timeline', 'top', ToastStyle.DANGER);
      return;
    }
    if (selActivity instanceof CaseActivity) {
      this.uiService.displayLoader();
      await this.caseDataService.findCaseById(selActivity.ID).then(async res => {
        if (res) {
          for (let key in res) {
            if (key.charAt(0) === "_") {
              var a = key.substring(1, key.length);
              res[a] = res[key];
              delete res[key];
            }
          }
          let iCase: CaseActivity;
          iCase = new CaseActivity(res);
          iCase = await this.caseService.reInitDataDependency(iCase);
          this.activityService.selectedActivity = iCase;
          this.caseService.accessedFrom = AccesingMode.ACCOUNT_TIMELINE;
          this.navService.pushChildNavPageWithPageTracking(PageName.CaseManagementDetailComponent, PageName.CaseManagementDetailComponent, PageName.AccountPageComponent, { from: 'AccountTimeline' });
          this.caseService.assignSelectedCase(iCase);
          this.footerService.initButtons(FooterViews.CUSTOMER_INQUIRY);
        }
        this.uiService.dismissLoader();
      }).catch(() => {
        this.uiService.dismissLoader();
      });
    } else if (selActivity instanceof OrderActivity) {
      let foundActivity = this.activityService.getActivityByID(selActivity.ID);
      if (foundActivity) {
        this.activityService.selectedActivity = foundActivity;
      } else {
        this.uiService.displayLoader();
        foundActivity = await this.activityDataService.getRealTimeActivityDetails(selActivity);
        this.uiService.dismissLoader();
      }
      this.activityService.selectedActivity = (foundActivity) ? foundActivity : selActivity;
      this.navService.pushChildNavPageWithPageTracking(PageName.OrderDetailsComponent, PageName.OrderDetailsComponent, PageName.AccountPageComponent, { from: 'AccountTimeline', activity: this.activityService.selectedActivity });
    } else if (selActivity instanceof ProcedureTrackerActivity) {
      let foundActivity = this.activityService.getActivityByID(selActivity.ID);
      if (foundActivity) {
        this.activityService.selectedActivity = foundActivity;
      } else {
        this.uiService.displayLoader();
        foundActivity = await this.activityDataService.getRealTimeActivityDetails(selActivity);
        this.uiService.dismissLoader();
      }
      if (!foundActivity) return;
      this.navService.pushChildNavPageWithPageTracking(PageName.ProcedureTrackerDetailsComponent, PageName.ProcedureTrackerDetailsComponent, PageName.AccountPageComponent, { from: 'AccountTimeline', activity: this.activityService.selectedActivity });
    } else if (selActivity instanceof AppointmentActivity) {

      let activity = this.activityService.getActivityByID(selActivity.ID);
      this.activityService.prevSelectedActivity = this.activityService.selectedActivity;

      if (activity) {
        this.activityService.prevSelectedActivity = this.activityService.selectedActivity;
        this.activityService.selected = activity;
      }
      this.uiService.displayLoader();
      activity = await this.activityDataService.updateActivityDetails(activity ? activity : selActivity);
      this.accountOfflineService.tempSelected = cloneDeep(this.accountOfflineService.selected);
      this.uiService.dismissLoader();
      this.activityService.selected = (activity) ? activity : selActivity;
      // fix to display missing info in detail page
      // this.activityService.selected = (this.activityService.selectedActivity) ? this.activityService.selectedActivity : tempActivity;
      this.activityService.updateSelectedActivityProps({ accessedFrom: PageName.AccountTimelineComponent });
      this.uiService.showNewActivity = false;
      this.activityDataService.activityDetailsLoaded = true;
      this.uiService.showNewActivity = false;
      this.activityDataService.activityDetailsLoaded = true;
      await this.navService.pushChildNavPageWithPageTracking(PageName.ActivitiesDetailsPaneComponent, PageName.ActivitiesDetailsPaneComponent, PageName.AccountPageComponent, { from: 'AccountTimeline' });
      //this.activityDataService.updateActivityDetails(activity);
      this.events.publish("detectChangesOnActivityDetails");
      if (this.activityService.selectedActivity) {
        switch (selActivity.type) {
          case ActivityType.Appointment:
            this.footerService.initButtons(FooterViews.Activities);
            break;
          default:
            this.footerService.initButtons('');
        }
      }

    } else if (selActivity instanceof StoreCheckActivity) {
      let activity = this.activityService.getActivityByID(selActivity.ID);
      this.activityService.prevSelectedActivity = this.activityService.selectedActivity;

      if (activity) {
        this.activityService.prevSelectedActivity = this.activityService.selectedActivity;
        this.activityService.selected = activity;
      }
      await this.uiService.displayLoader();
      let tempActivity: StoreCheckActivity = new StoreCheckActivity({
        activityId: selActivity.ID,
        activitytypecode: "appointment",
        indskr_ownerfullname: selActivity.meetingOwner,
        indskr_ownerid: selActivity.ownerId,
        location: selActivity.ownerId,
        scheduledend: selActivity.scheduledEnd.getTime().toString(),
        scheduledstart: selActivity.scheduledStart.getTime().toString(),
        statuscode: selActivity.status,
        statecode: selActivity.state,
        subject: selActivity.subject,
        indskr_type: ActivityType.StoreCheck
      });
      this.activityService.selected = (activity) ? activity : tempActivity;
      await this.activityDataService.updateActivityDetails(activity ? activity : tempActivity);
      this.accountOfflineService.tempSelected = cloneDeep(this.accountOfflineService.selected);
      this.uiService.dismissLoader();
      this.activityService.updateSelectedActivityProps({ accessedFrom: PageName.AccountTimelineComponent });
      this.uiService.showNewActivity = false;
      this.activityDataService.activityDetailsLoaded = true;
      this.uiService.showNewActivity = false;
      this.activityDataService.activityDetailsLoaded = true;
      await this.navService.pushChildNavPageWithPageTracking(PageName.StoreCheckDetailsComponent, PageName.StoreCheckDetailsComponent, PageName.AccountPageComponent, { from: 'AccountTimeline' });
    } else if (selActivity instanceof PhoneActivity) {
      let activity = this.activityService.getActivityByID(selActivity.ID);
      if (activity) {
        this.activityService.prevSelectedActivity = this.activityService.selectedActivity;
        this.activityService.selected = activity;
      }
      await this.uiService.displayLoader();
      //activity = await this.activityDataService.getRealTimeActivityDetails(selActivity);
      // await this.activityDataService.updateActivityDetails(activity);
      await this.activityDataService.getPhonecallActivityByIdOnline(selActivity.ID, selActivity.scheduledStart.getTime().toString(), selActivity.scheduledEnd.getTime().toString(), selActivity.scheduledStart.getTime().toString()).then(async (response) => {
        activity = new PhoneActivity(response);
        await this.activityDataService.updateActivityForConfigField(activity, response, ActivityType.PhoneCall);
        await this.activityDataService._appendPhoneCallDetailsToActivity(activity, response, false);
        this.accountOfflineService.tempSelected = cloneDeep(this.accountOfflineService.selected);
        this.activityDataService.activityDetailsLoaded = true;
      })
      await this.uiService.dismissLoader();
      this.activityService.selected = activity;
      this.activityService.updateSelectedActivityProps({ accessedFrom: PageName.AccountTimelineComponent });
      this.uiService.showNewActivity = false;
      this.activityDataService.activityDetailsLoaded = true;
      await this.navService.pushChildNavPageWithPageTracking(PageName.PhoneCallDetailsComponent, PageName.PhoneCallDetailsComponent, PageName.AccountPageComponent, { from: 'AccountTimeline' });

      this.events.publish("detectChangesOnPhoneCallActivityDetails");
      this.footerService.initButtons(FooterViews.PHONE_CALL);
    } else if (selActivity instanceof SurgeryOrderActivity) {
      let foundActivity = this.activityService.getActivityByID(selActivity.ID);
      if (foundActivity) {
        this.activityService.selectedActivity = foundActivity;
      } else {
        this.uiService.displayLoader();
        foundActivity = await this.activityDataService.getRealTimeActivityDetails(selActivity);
        this.uiService.dismissLoader();
      }
      this.activityService.selectedActivity = (foundActivity) ? foundActivity : selActivity;
      this.navService.pushChildNavPageWithPageTracking(PageName.SurgeryOrderDetailsComponent, PageName.SurgeryOrderDetailsComponent, PageName.AccountPageComponent, { from: 'AccountTimeline', activity: this.activityService.selectedActivity });
      this.footerService.initButtons(FooterViews.SURGERY_ORDER_ACTIVITY);
    } else if (selActivity instanceof EventActivity) {
      const eventsToolData = this.eventsToolService.eventsToolData;
      const foundEvent = eventsToolData.find(e => {
        if (e.ID) {
          return e.ID === selActivity.ID;
        } else if (e.offlineId) {
          return e.offlineId === selActivity.ID;
        } else {
          return false;
        }
      });
      if (!isEmpty(foundEvent)) {
        selActivity = foundEvent;
        this.eventsToolService.selectedEventOnEventsTool = foundEvent;
      } else {
        return;
      }
      if (!this.device.isOffline) {
        this.uiService.displayLoader();
        await this.eventsToolDataService.fetchEventRealTimeDetails(selActivity as EventActivity).then((updatedEvent: EventActivity) => {
          selActivity = updatedEvent;
          this.uiService.dismissLoader();
        }).catch(err => {
          console.error(err);
          this.uiService.dismissLoader();
        });
      }
      this.activityService.prevSelectedActivity = this.activityService.selectedActivity;
      this.activityService.selectedActivity = selActivity;
      this.navService.pushChildNavPageWithPageTracking(PageName.EventDetailsPageComponent, PageName.EventDetailsPageComponent, PageName.AccountPageComponent, {
        from: 'AccountTimeline', event: this.activityService.selectedActivity,
      });
      this.eventsToolService.setSelectedEventUpdates(this.activityService.selectedActivity as EventActivity);
      this.footerService.initButtons(FooterViews.EVENTS_DETAILS);
    } else if (selActivity.type == ActivityType.CustomerSurvey) {
      if (this.device.isOffline) {
        this.notifiationService.notify(this.translate.instant("SURVEY_ACTIVITIES_NOTIFY_ONLINE"), "survey-activities", "top", ToastStyle.DANGER, 3000, true);
        return;
      }
      if (!isEmpty(selActivity)) {
        this.openSubmittedSurveyDetails(selActivity);
      }
    } else if(selActivity instanceof EmailActivity) {
      let activity = this.activityService.getActivityByID(selActivity.ID);
      this.activityService.prevSelectedActivity = this.activityService.selectedActivity;
      if (activity) {
        this.activityService.prevSelectedActivity = this.activityService.selectedActivity;
        this.activityService.selected = activity;
      }
      await this.uiService.displayLoader();
      activity = await this.activityDataService.updateActivityDetails(activity ? activity : selActivity);
      this.accountOfflineService.tempSelected = cloneDeep(this.accountOfflineService.selected);
      await this.uiService.dismissLoader();
      this.activityService.selected = (activity) ? activity : selActivity;
      this.activityService.updateSelectedActivityProps({ accessedFrom: PageName.AccountTimelineComponent });
      this.uiService.showNewActivity = false;
      this.activityDataService.activityDetailsLoaded = true;
      this.uiService.showNewActivity = false;
      this.activityDataService.activityDetailsLoaded = true;
      await this.navService.pushChildNavPageWithPageTracking(PageName.EmailDetailsPageComponent, PageName.EmailDetailsPageComponent, PageName.AccountPageComponent, { from: 'AccountTimeline' });
      
    }
  }

  /** Search Bar Filter **/
  public async openTimelineFilter(event) {
    const modal = await this.modalCtrl.create({
      component: IndFilterMenuModalComponent,
      cssClass: 'filter-menu-right-modal',
      backdropDismiss: true,
      componentProps: {
        viewData: {
          from: PageName.AccountTimelineComponent,
          data: this.accountTimelineFilterFormView,
          filterTitle: this.translate.instant('ALL_FILTERS'),
          selectedFilters: this.selectedFilters,
          selectedDate: this.selectedDate,
          isDisplayDateField: this.from !== 'opportunities-list-section' && this.from !== 'salesorder-section' && this.from !== 'contract-section'
        },
      }
    });

    await modal.present();
    const res = await modal.onDidDismiss();

    if (res && res.data && res.data.isDone) {
      if (!isEmpty(res.data.selectedItems) || !isEmpty(res.data.selectedDate)) {
        this.accountTimelineFilterFormView = res.data.selectedItems;
        this.selectedFilters = res.data.selectedItemsAccordion;

        const isFilteredByDate = !isEmpty(res.data.selectedDate?.startDate) && !isEmpty(res.data.selectedDate?.endDate);
        this.selectedDate = isFilteredByDate ? {
          startDate: res.data.selectedDate.startDate,
          endDate: res.data.selectedDate.endDate,
        } : {
          startDate: '',
          endDate: '',
        };

        // Reset pagination when applying filters (only for timeline view)
        if (this.from === 'timeline') {
          this.page = 1;
        }
        
        if (this.from === 'opportunities-list-section') {
          this._applyAdvancedFilterOnOpportunities();
        } else if (this.from === 'salesorder-section') {
          this._applyAdvancedFilterOnSalesOrders();
        } else if (this.from === 'contract-section') {
          this._applyAdvancedFilterOnProductContract()
        } else if (this.from === 'quotes-section') {
          this._applyAdvancedFilterOnQuotesSection();
        } else if (this.from === 'asset-management-section') {
          this._applyAdvancedFilterOnAssetManagement();
        } else if (this.from === 'accountPlan-section') {
          this._applyAdvancedFilterOnAccountPlan();
        }
         else {
          this._applyAdvancedFilterOnTimelineActivities();
        }
      }
    }
  }

  private _applyAdvancedFilterOnQuotesSection() {
    if (isEmpty(this.accountTimelineFilterFormView)) {
      this._filteredTimelineActivities = this._allTimelineActivities;
      this._initTimelineItemsViewData();
      this._initSectionHeadersView();
      return;
    }

    this.filterButtonBadgeCount = this.selectedFilters.length;

    // Collect all selected statuses and products
    const selectedStatuses = new Set(this.selectedFilters
      .filter(filter => filter.category === 'Status')
      .map(filter => filter.value));

    const selectedProducts = new Set(this.selectedFilters
      .filter(filter => filter.category === 'Products')
      .map(filter => filter.value));

    // Apply status and product filters
    this._filteredTimelineActivities = this._allTimelineActivities.filter((quote: Quotes) => {
      const statusMatch = selectedStatuses.size > 0 ? selectedStatuses.has(this.quotesService.getFormattedStatus(quote.statuscode)) : true;
      const productMatch = selectedProducts.size > 0 ? quote.products.some((prod: Product) => selectedProducts.has(prod.name)) : true;
      return statusMatch && productMatch;
    });

    this._initTimelineItemsViewData();
    this._initSectionHeadersView();
  }

  private _applyAdvancedFilterOnSalesOrders() {
    if (isEmpty(this.accountTimelineFilterFormView)) {
      this._filteredTimelineActivities = this._allTimelineActivities;
      this._initTimelineItemsViewData();
      this._initSectionHeadersView();
      return;
    }

    this.filterButtonBadgeCount = this.selectedFilters.length;
    if (!isEmpty(this.selectedDate) && (!isEmpty(this.selectedDate.startDate) || !isEmpty(this.selectedDate.endDate))) {
      this.filterButtonBadgeCount += 1;
    }

    let targetActivities = Object.assign([], this._allTimelineActivities);

    // Collect all selected statuses
    const selectedStatuses = this.selectedFilters
      .filter(filter => filter.category === 'Status')
      .map(filter => filter.value);

    // Collect all selected products
    const selectedProducts = this.selectedFilters
      .filter(filter => filter.category === 'Products')
      .map(filter => filter.value);

    // Apply status and product filters
    if (selectedStatuses.length > 0 || selectedProducts.length > 0) {
      targetActivities = targetActivities.filter(order => {
        const statusMatch = selectedStatuses.length > 0 ? selectedStatuses.includes(order.orderStatusString) : true;
        const productMatch = selectedProducts.length > 0 ? order.products.some(prod => selectedProducts.includes(prod.productName)) : true;
        return statusMatch && productMatch;
      });
    }

    this._filteredTimelineActivities = targetActivities;
    this._initTimelineItemsViewData();
    this._initSectionHeadersView();
  }

  private _applyAdvancedFilterOnTimelineActivities() {
    if (!isEmpty(this.accountTimelineFilterFormView) || !isEmpty(this.selectedDate)) {
      this.filterButtonBadgeCount = 0;
      this._filteredTimelineActivities = [];
      //this.accountTimelineFilterFormView = res.data.selectedItems;
      this.isTimelineFiltered = true;
      //this.selectedFilters = res.data.selectedItemsAccordion;
      this.filterButtonBadgeCount = this.selectedFilters.length;
      if (!isEmpty(this.selectedDate) && (!isEmpty(this.selectedDate.startDate) || !isEmpty(this.selectedDate.endDate))) {
        this.filterButtonBadgeCount = this.filterButtonBadgeCount + 1;
      }
      let selectedActivitiesFromMainFilter = [];
      this.selectedFilters.forEach(item => {
        if (item.type == 'filter-main') {
          selectedActivitiesFromMainFilter.push(item.category);
        }
      });

      /******* filtered activities by duration *******/
      let isFilteredByDate: boolean = !isEmpty(this.selectedDate) && !isEmpty(this.selectedDate.startDate) && !isEmpty(this.selectedDate.endDate) || false;
      this.onTimelineTypeChange(this.accountTimelineFilter);
      let targetTimelineActivities = cloneDeep(this._filteredTimelineActivities);
      if (isFilteredByDate) {
        targetTimelineActivities = this._filterTimelineActivitiesByDate(targetTimelineActivities, this.selectedDate);
      } else {

      }

      /******* filtered activities by selected activity and options *******/
      const isSelectedMyActivity: boolean = !isEmpty(selectedActivitiesFromMainFilter) && selectedActivitiesFromMainFilter.includes(ActivityType.MyActivity);
      if (isSelectedMyActivity) targetTimelineActivities = targetTimelineActivities.filter(activity => activity.ownerId == this.ownerId);
      if (!isEmpty(selectedActivitiesFromMainFilter)) {
        let activities = [];
        selectedActivitiesFromMainFilter.forEach(mainFilterValue => {
          if (mainFilterValue != ActivityType.MyActivity) {
            let filteredTargetTimelineActivity = [];
            if (mainFilterValue == ActivityType.LiveMeet) {
              filteredTargetTimelineActivity = targetTimelineActivities.filter(activity => activity.type == ActivityType.Appointment && activity.location == 'LiveMeet');
            } else {
              filteredTargetTimelineActivity = targetTimelineActivities.filter(activity => activity.type == mainFilterValue);
            }
            const filteredSelectedItemsByActivity = this.selectedFilters.filter(item => item.parentId == mainFilterValue);
            const groupedCategory = groupBy(filteredSelectedItemsByActivity, 'category');
            const groupedCategoryList = values(groupedCategory);
            if (!isEmpty(filteredSelectedItemsByActivity)) {
              filteredTargetTimelineActivity.forEach(act => {
                let isMatchedData: boolean = true;
                groupedCategoryList.forEach(category => {
                  if (mainFilterValue == ActivityType.Appointment && act.location != 'LiveMeet') {
                    if (category[0]['type'] == 'appointment-status') {
                      isMatchedData = category.some(item => item.id == ActivityType.Appointment + '_' + act.statusString) && isMatchedData;
                    } else if (category[0]['type'] == 'appointment-product') {
                      isMatchedData = this._matchedProduct(ActivityType.Appointment, category, act) && isMatchedData;
                    }
                  }
                  else if (mainFilterValue == ActivityType.Email) {
                    if (category[0]['type'] == 'messages-channelType') {
                      isMatchedData = category.some(item => item.id == ActivityType.Email + '_' + act.channelName) && isMatchedData;
                    } else if (category[0]['type'] == 'messages-status') {
                      isMatchedData = category.some(item => item.id == ActivityType.Email + '_' + act.status) && isMatchedData
                    } else if (category[0]['type'] == 'messages-transfer-status') {
                      let formattedTransferStatus: string = '';
                      if (act.status == 548910000) {
                        if (act.actualEnd && (act.channelName == 'SMS' || act.channelName == 'Whatsapp')) {
                          formattedTransferStatus = this.translate.instant('SHARED');
                        }
                      } else {
                        if (act.channelName == 'Email' || act.channelName == undefined) {
                          if (act.senton && !act.readOn) {
                            formattedTransferStatus = this.translate.instant('DELIVERED');
                          } else if (act.readOn) {
                            formattedTransferStatus = this.translate.instant('READ');
                          }
                        } else if (act.channelName == 'SMS' || act.channelName == 'Whatsapp') {
                          if (act.senton && !act.readOn) {
                            formattedTransferStatus = this.translate.instant('SENT');
                          } else if (act.readOn) {
                            formattedTransferStatus = this.translate.instant('DELIVERED');
                          }
                        }
                      }
                      isMatchedData = category.some(item => item.id == ActivityType.Email + '_' + formattedTransferStatus) && isMatchedData;
                    }
                  }
                  else if (mainFilterValue == ActivityType.CaseIntake) {
                    if (category[0]['type'] == 'caseIntake-status') {
                      isMatchedData = category.some(item => item.id == ActivityType.CaseIntake + '_' + act._case_status_value) && isMatchedData;
                    } else if (category[0]['type'] == 'caseIntake-stage') {
                      isMatchedData = category.some(item => item.id == ActivityType.CaseIntake + '_' + act._case_stage_value) && isMatchedData;
                    } else if (category[0]['type'] == 'caseIntake-assigned-to') {
                      isMatchedData = category.some(item => item.id == ActivityType.CaseIntake + '_' + act.meetingOwnerId) && isMatchedData;
                    } else if (category[0]['type'] == 'caseIntake-product') {
                      isMatchedData = category.some(item => item.id == ActivityType.CaseIntake + '_' + act._case_trans_product_name) && isMatchedData;
                    }
                  }
                  else if (mainFilterValue == ActivityType.Event) {
                    if (category[0]['type'] == 'event-status') {
                      isMatchedData = category.some(item => item.id == ActivityType.Event + '_' + act.status) && isMatchedData;
                    }
                  }
                  else if (mainFilterValue == ActivityType.LiveMeet) {
                    if (category[0]['type'] == 'liveMeeting-status') {
                      isMatchedData = category.some(item => item.id == ActivityType.LiveMeet + '_' + act.statusString) && isMatchedData;
                    } else if (category[0]['type'] == 'liveMeeting-product') {
                      isMatchedData = this._matchedProduct(ActivityType.LiveMeet, category, act) && isMatchedData;
                    }
                  }
                  else if (mainFilterValue == ActivityType.PhoneCall) {
                    if( category[0]['type'] == "phoneCall-status") {
                      isMatchedData = category.some(item => item.id == ActivityType.PhoneCall + '_' + act.statusString) && isMatchedData;
                    }
                  }
                  else if (mainFilterValue == ActivityType.SurgeryOrder) {
                    if( category[0]['type'] == "surgeryOrder-status") {
                      isMatchedData = category.some(item => item.id == ActivityType.SurgeryOrder + '_' + act.statusString) && isMatchedData;
                    } else if (category[0]['type'] == 'surgeryOrder-product') {
                      isMatchedData = this._matchedProduct(ActivityType.SurgeryOrder, category, act) && isMatchedData;
                    }
                  }
                  else if (mainFilterValue == ActivityType.WebsiteAccess) {
                    if (category[0]['type'] == 'websiteAccessLogs-channels') {
                      isMatchedData = category.some(item => item.id == ActivityType.WebsiteAccess + '_' + act.channel) && isMatchedData;
                    } else if (category[0]['type'] == 'websiteAccessLogs-department') {
                      isMatchedData = category.some(item => item.id == ActivityType.WebsiteAccess + '_' + act.department) && isMatchedData;
                    }
                  }
                  else if (mainFilterValue == ActivityType.Order) {
                    if (category[0]['type'] == 'salesOrder-status') {
                      isMatchedData = category.some(item => item.id == ActivityType.Order + '_' + act.statusString) && isMatchedData;
                    } else if (category[0]['type'] == 'salesOrder-owner') {
                      isMatchedData = category.some(item => item.id == ActivityType.Order + '_' + act.ownerId) && isMatchedData;
                    } else if (category[0]['type'] == 'salesOrder-product') {
                      isMatchedData = this._matchedProduct(ActivityType.Order, category, act) && isMatchedData;
                    }
                  }
                  else if (mainFilterValue == ActivityType.CustomerSurvey) {
                    if (category[0]['type'] == 'customerSurvey-surveyType') {
                      isMatchedData = category.some(item => item.id == ActivityType.CustomerSurvey + '_' + act.type) && isMatchedData;
                    } else if (category[0]['type'] == 'customerSurvey-surveyFrequency') {
                      isMatchedData = category.some(item => item.id == ActivityType.CustomerSurvey + '_' + act.surveyFrequency) && isMatchedData;
                    } else if (category[0]['type'] == 'customerSurvey-surveyedBy') {
                      isMatchedData = category.some(item => item.id == ActivityType.CustomerSurvey + '_' + act.surveyedBy) && isMatchedData;
                    }
                  }
                });

                if (isMatchedData) activities.push(act);

              });
            } else {
              let filteredActivityMainOnly = targetTimelineActivities.filter(activity => activity.type == mainFilterValue);
              filteredActivityMainOnly.forEach(act => {
                activities.push(act);
              });
            }
          }
        });
        if (!isSelectedMyActivity) targetTimelineActivities = activities;
      }

      /******* grouped filtered activities *******/
      this._filteredTimelineActivities = targetTimelineActivities;
      this._initTimelineItemsViewData();
    } else {
      this.onTimelineTypeChange(this.accountTimelineFilter);
    }
    // Enable infinite scroll after applying filters
    if (this.infiniteScroll) {
      this.infiniteScroll.disabled = false;
    }
  }

  private _matchedProduct(activityType: ActivityType, category: any[], act: any): boolean {
    let isMatchedData: boolean = false;
    isMatchedData = category.some(item => {
      let isFoundProduct: boolean = false;
      if (!isEmpty(act.products)) {
        act.products.forEach(product => {
          const temId: string = product.productId || product.ID;
          const formattedId = activityType + '_' + temId;
          if (item.id == formattedId) return isFoundProduct = true;
        });
      }
      return isFoundProduct
    })
    return isMatchedData;
  }

  private _filterTimelineActivitiesByDate(targetTimelineActivities: any, selectedDate: any) {
    const startDate: Date = new Date(selectedDate.startDate);
    const endDate: Date = new Date(selectedDate.endDate);
    targetTimelineActivities.map(activity => {
      if (activity.hasOwnProperty('scheduledStart') && activity['scheduledStart'] != '' && activity['scheduledStart'] != "Invalid Date" && activity['scheduledStart'] != undefined) {
        activity['isValidStartDate'] = true;
        activity['scheduledStart'] = new Date(activity['scheduledStart']);
      }
      if (activity.hasOwnProperty('scheduledEnd') && activity['scheduledEnd'] != '' && activity['scheduledEnd'] != "Invalid Date" && activity['scheduledEnd'] != undefined) {
        activity['isValidEndDate'] = true;
        activity['scheduledEnd'] = new Date(activity['scheduledEnd']);
      }
    });
    let filteredTimelineActivities = [];
    targetTimelineActivities.forEach(activity => {
      if (activity.hasOwnProperty('scheduledStart') && activity['isValidStartDate'] && activity.hasOwnProperty('scheduledEnd') && activity['isValidEndDate']) {
        if (activity['scheduledStart'] >= startDate && activity['scheduledEnd'] <= endDate) {
          filteredTimelineActivities.push(activity);
        }
      } else if (activity.hasOwnProperty('scheduledStart') && activity['isValidStartDate']) {
        if (activity['scheduledStart'] >= startDate && activity['scheduledStart'] <= endDate) {
          filteredTimelineActivities.push(activity);
        }
      } else if (activity.hasOwnProperty('scheduledEnd') && activity['isValidEndDate']) {
        if (activity['scheduledEnd'] <= endDate) {
          filteredTimelineActivities.push(activity);
        }
      }
    });
    // targetTimelineActivities = filteredTimelineActivities;
    return filteredTimelineActivities;
  }

  /**
   * Filters timeline items based on search input
   * @param items Array of timeline items to filter
   * @returns Filtered array of timeline items
   */
  private _filterTimelineItemsBySearch(items: any[]): any[] {
    if (!this.searchInput || this.searchInput.length < 2) {
      return items;
    }

    return items.filter(item => {
      let flag: boolean = false;
      if (item.headerText) {
        flag = (item.headerText.toLocaleLowerCase().indexOf(this.searchInput.toLocaleLowerCase()) > -1);
      }
      if (!flag && item.subheaderText) {
        flag = (item.subheaderText.toLocaleLowerCase().indexOf(this.searchInput.toLocaleLowerCase()) > -1);
      }
      if (!flag && item.subheaderText2) {
        flag = (item.subheaderText2.toLocaleLowerCase().indexOf(this.searchInput.toLocaleLowerCase()) > -1);
      }
      if (!flag && item.dataToDisplay && item.dataToDisplay.length > 0) {
        for (let i = 0; i < item.dataToDisplay.length; i++) {
          const dtdItem = item.dataToDisplay[i];
          if (dtdItem && dtdItem.value) {
            flag = (dtdItem.value.toLocaleLowerCase().indexOf(this.searchInput.toLocaleLowerCase()) > -1);
          }
          if (flag) {
            break;
          }
        }
      }
      return flag;
    });
  }

  getSelectedText(value) {
    const tot = this._filteredTimelineViewActivities.length;
    if ((this.filterButtonBadgeCount && this.filterButtonBadgeCount > 0) || (this.searchInput && this.searchInput.length >= 2)) {
      return this.translate.instant('ACCOUNT_RESULTS') + ` ` + `(${tot})`;
    }
    switch (value) {
      case ActivityType.AllActivity:
        return `${this.translate.instant('ALL_ACTIVITIES')} (${tot})`;
      case ActivityType.MyActivity:
        return `${this.translate.instant('MY_ACTIVITIES')} (${tot})`;
      case ActivityType.Appointment:
        return `${this.translate.instant('MEETINGS')} (${tot})`;
      case ActivityType.Email:
        return `${this.translate.instant('MESSAGES')} (${tot})`;
      case ActivityType.Sample:
        return `${this.translate.instant('ALLOWCATION_ORDERS')} (${tot})`;
      case ActivityType.CaseIntake:
        return `${this.utilityService.globalCustomerText} Inquiries (${tot})`;
      case ActivityType.Order:
        return `${this.translate.instant('SALES_ORDERS')} (${tot})`;
      case ActivityType.Event:
        return `${this.translate.instant('EVENTS')} (${tot})`;
      case ActivityType.SurgeryOrder:
        return `${this.translate.instant('PROCEDURE_LOGS')} (${tot})`;
      case ActivityType.PhoneCall:
        return `${this.translate.instant('PHONE_CALLS')} (${tot})`;
      case ActivityType.ProcedureTracker:
        return `${this.translate.instant('BULK_PROCEDURE_LOGS')} (${tot})`;
      case ActivityType.CustomerSurvey:
        return `${this.translate.instant('SURVEY')} (${tot})`;
      default:
        return `${this.translate.instant('ALL_ACTIVITIES')} (${tot})`;
    }
  }

  private _initFilterFormViews() {
    this.accountTimelineFilterFormView = [];
    if (!isEmpty(this._allTimelineActivities)) {
      let filterFormViewMain: IndFilterMenuModalDataModel = {
        id: 'filter-main',
        displayName: this.translate.instant('ACTIVITY'),
        isMultiSelect: true,
        options: []
      }
      this.accountTimelineFilterFormView.push(filterFormViewMain);

      let myActivitesDisplayText = this.translate.instant('MY_ACTIVITIES');
      let meetingsDisplayText = this.translate.instant('MEETINGS');
      if (this.translate.currentLang == 'it') {
        myActivitesDisplayText = this.translate.instant('MY_ACTIVITIES_TIMELINE');
        meetingsDisplayText = this.translate.instant('MEETINGS_TIMELINE');
      }
      //default filter options - my activity
      filterFormViewMain.options.push({ id: 'myActivities-main', group: ActivityType.MyActivity, isSelected: false, displayName: myActivitesDisplayText });
      //add filter options based on the timelineActivities
      //Appointment
      filterFormViewMain.options.push({ id: 'meeting-main', group: ActivityType.Appointment, isSelected: false, displayName: meetingsDisplayText });
      //status
      const meetingStatusOption = [{
        group: this.translate.instant('STATUS'),
        id: ActivityType.Appointment + '_' + this.translate.instant('OPEN'),
        displayName: this.translate.instant('OPEN'),
        isSelected: false
      }, {
        group: this.translate.instant('STATUS'),
        id: ActivityType.Appointment + '_' +this.translate.instant('COMPLETED'),
        displayName: this.translate.instant('COMPLETED'),
        isSelected: false
      }, {
        group: this.translate.instant('STATUS'),
        id: ActivityType.Appointment + '_' + this.translate.instant('CANCELLED'),
        displayName: this.translate.instant('CANCELLED'),
        isSelected: false
      }]
      const statusViewData = {
        id: 'appointment-status',
        parentId: ActivityType.Appointment,
        displayName: this.translate.instant('MEETINGS') + ' - ' + this.translate.instant('STATUS'),
        isMultiSelect: true,
        options: meetingStatusOption
      }
      this.accountTimelineFilterFormView.push(statusViewData);
      const productOptions = this.getProductOptions(ActivityType.Appointment);
      //Products
      const productsViewData = {
        id: 'appointment-product',
        parentId: ActivityType.Appointment,
        displayName: this.translate.instant('MEETINGS') + ' - ' + this.translate.instant('PRODUCT'),
        isMultiSelect: true,
        options: productOptions
      }
      this.accountTimelineFilterFormView.push(productsViewData);
      //Email
      if (this.authenticationService.hasFeatureAction(FeatureActionsMap.EMAIL_ACTIVITY)) {
        filterFormViewMain.options.push({ id: 'email-main', group: ActivityType.Email, isSelected: false, displayName: this.translate.instant('MESSAGES') });
        //Channel type
        const channelViewData = {
          id: 'messages-channelType',
          parentId: ActivityType.Email,
          displayName: this.translate.instant('MESSAGES') + ' - ' + this.translate.instant('CHANNELS'),
          isMultiSelect: true,
          options: []
        }
        this.directMessagingService.channelList.pipe(takeUntil(this.ngDestroy$)).subscribe(channels => {
          const channelList = channels.filter(c => c.activityType === ChannelActivityType.EMAIL || c.activityType === ChannelActivityType.SMS || c.activityType === ChannelActivityType.WHATSAPP) || [];
          channelList.forEach(channel => {
            channelViewData.options.push({
              group: this.translate.instant('CHANNELS'),
              id: ActivityType.Email + '_' + channel.title,
              displayName: channel.title,
              isSelected: false,
            });
          });
        })
        this.accountTimelineFilterFormView.push(channelViewData);
        //Status
        const statusViewData = {
          id: 'messages-status',
          parentId: ActivityType.Email,
          displayName: this.translate.instant('MESSAGES') + ' - ' + this.translate.instant('STATUS'),
          isMultiSelect: true,
          options: [{
            group: this.translate.instant('STATUS'),
            id: ActivityType.Email + '_' + EmailStatusCodes.Sent,
            displayName: this.translate.instant('SENT'),
            isSelected: false,
          }, {
            group: this.translate.instant('STATUS'),
            id: ActivityType.Email + '_' + EmailStatusCodes.Shared,
            displayName: this.translate.instant('SHARED'),
            isSelected: false,
          },{
            group: this.translate.instant('STATUS'),
            id: ActivityType.Email + '_' + EmailStatusCodes.PendingSend,
            displayName: this.translate.instant('PENDING_SYNC'),
            isSelected: false,
          }]
        }
        this.accountTimelineFilterFormView.push(statusViewData);
        //Transfer status
        const transferStatusViewData = {
          id: 'messages-transfer-status',
          parentId: ActivityType.Email,
          displayName: this.translate.instant('MESSAGES') + ' - ' + this.translate.instant('TRANSFER_STATUS'),
          isMultiSelect: true,
          options: [{
            group: this.translate.instant('TRANSFER_STATUS'),
            id: ActivityType.Email + '_' + EmailStatusCodes.Shared,
            displayName: this.translate.instant('SHARED'),
            isSelected: false,
          }, {
            group: this.translate.instant('TRANSFER_STATUS'),
            id: ActivityType.Email + '_' + this.translate.instant('DELIVERED'),
            displayName: this.translate.instant('DELIVERED'),
            isSelected: false,
          }, {
            group: this.translate.instant('TRANSFER_STATUS'),
            id: ActivityType.Email + '_' + this.translate.instant('READ'),
            displayName: this.translate.instant('READ'),
            isSelected: false,
          }, {
            group: this.translate.instant('TRANSFER_STATUS'),
            id: ActivityType.Email + '_' + this.translate.instant('SENT'),
            displayName: this.translate.instant('SENT'),
            isSelected: false,
          }]
        }
        this.accountTimelineFilterFormView.push(transferStatusViewData);
      }
      if (this.authenticationService.hasFeatureAction(FeatureActionsMap.CASE_INTAKE)) {
        filterFormViewMain.options.push({ id: 'customerInquiries-main', group: ActivityType.CaseIntake, isSelected: false, displayName: this.translate.instant('CUSTOMER_INQUIRIES', { globalCustomerText: this.utilityService.globalCustomerText }) });
        //status
        const statusViewData = {
          id: 'caseIntake-status',
          parentId: ActivityType.CaseIntake,
          displayName: this.translate.instant('INQUIRY') + ' - ' + this.translate.instant('STATUS'),
          isMultiSelect: true,
          options: [{
            group: this.translate.instant('STATUS'),
            id: ActivityType.CaseIntake + '_' + this.translate.instant('IN_PROGRESS'),
            displayName: this.translate.instant('IN_PROGRESS'),
            isSelected: false,
          }, {
            group: this.translate.instant('STATUS'),
            id: ActivityType.CaseIntake + '_' + this.translate.instant('ON_HOLD'),
            displayName: this.translate.instant('ON_HOLD'),
            isSelected: false,
          }, {
            group: this.translate.instant('STATUS'),
            id: ActivityType.CaseIntake + '_' + this.translate.instant('WAITING_FOR_DETAILS'),
            displayName: this.translate.instant('WAITING_FOR_DETAILS'),
            isSelected: false,
          },
          {
            group: this.translate.instant('STATUS'),
            id: ActivityType.CaseIntake + '_' + this.translate.instant('RESEARCHING'),
            displayName: this.translate.instant('RESEARCHING'),
            isSelected: false,
          }, {
            group: this.translate.instant('STATUS'),
            id: ActivityType.CaseIntake + '_' + this.translate.instant('PROBLEM_SOLVED'),
            displayName: this.translate.instant('PROBLEM_SOLVED'),
            isSelected: false,
          }, {
            group: this.translate.instant('STATUS'),
            id: ActivityType.CaseIntake + '_' + this.translate.instant('CANCELLED'),
            displayName: this.translate.instant('CANCELLED'),
            isSelected: false,
          }, {
            group: this.translate.instant('STATUS'),
            id: ActivityType.CaseIntake + '_' + this.translate.instant('INFORMATION_PROVIDED'),
            displayName: this.translate.instant('INFORMATION_PROVIDED'),
            isSelected: false,
          }]
        }
        this.accountTimelineFilterFormView.push(statusViewData);
        //stage
        //assign to
        //Products
        const productsViewData = {
          id: 'caseIntake-product',
          parentId: ActivityType.CaseIntake,
          displayName: this.translate.instant('INQUIRY') + ' - ' + this.translate.instant('PRODUCT'),
          isMultiSelect: true,
          options: this.getProductOptions(ActivityType.CaseIntake)
        }
        this.accountTimelineFilterFormView.push(productsViewData);
      }
      if (this.authenticationService.hasFeatureAction(FeatureActionsMap.EVENT_TOOL)) {
        filterFormViewMain.options.push({ id: 'event-main', group: ActivityType.Event, isSelected: false, displayName: this.translate.instant('EVENTS') });
        //status
        const statusViewData = {
          id: 'event-status',
          parentId: ActivityType.Event,
          displayName: this.translate.instant('EVENTS') + ' - ' + this.translate.instant('STATUS'),
          isMultiSelect: true,
          options: [{
            group: this.translate.instant('STATUS'),
            id: ActivityType.Event + '_' + 548910005,
            displayName: this.translate.instant("COMPLETED"),
            isSelected: false,
          }]
        }
        this.accountTimelineFilterFormView.push(statusViewData);
      }
      filterFormViewMain.options.push({ id: 'liveMeeting-main', group: ActivityType.LiveMeet, isSelected: false, displayName: this.translate.instant('LIVE_MEETING') });
      const viewData = {
        id: 'liveMeeting-status',
        parentId: ActivityType.LiveMeet,
        displayName: this.translate.instant('LIVE_MEETING') + ' - ' + this.translate.instant('STATUS'),
        isMultiSelect: true,
        options: meetingStatusOption
      }
      this.accountTimelineFilterFormView.push(viewData);
      const salesOrderStatus = [{
        group: this.translate.instant('STATUS'),
        id: ActivityType.Order + '_' + this.translate.instant("APPROVED"),
        displayName: this.translate.instant("APPROVED"),
        isSelected: false,
      }, {
        group: this.translate.instant('STATUS'),
        id: ActivityType.Order + '_' + this.translate.instant("OM_FULFILLED"),
        displayName: this.translate.instant("OM_FULFILLED"),
        isSelected: false,
      }];
      // New order flow (OMNI-60747): its orders take any status, so the statuses offered are the ones listed.
      const newOrderFlow = this.authenticationService.hasFeatureAction(FeatureActionsMap.NEW_ORDER_FLOW);
      if (newOrderFlow || this.authenticationService.hasFeatureAction(FeatureActionsMap.ORDER_MANAGEMENT)) {
        filterFormViewMain.options.push({ id: 'salesOrder-main', group: ActivityType.Order, isSelected: false, displayName: this.translate.instant('ACCOUNT_TIMELINE_OPTION_ORDERS') });
        const salesOrderviewData = {
          id: ORDER_STATUS_FILTER,
          parentId: ActivityType.Order,
          displayName: this.translate.instant('ACCOUNT_TIMELINE_OPTION_ORDERS') + ' - ' + this.translate.instant('STATUS'),
          isMultiSelect: true,
          options: newOrderFlow ? this.orderCaptureStatusOptions() : salesOrderStatus
        }
        this.accountTimelineFilterFormView.push(salesOrderviewData);
      }
      if (!newOrderFlow && this.authenticationService.hasFeatureAction(FeatureActionsMap.ORDER_MANAGEMENT)) {
        //Products
        const productsViewData = {
          id: 'salesOrder-product',
          parentId: ActivityType.Order,
          displayName: this.translate.instant('ACCOUNT_TIMELINE_OPTION_ORDERS') + ' - ' + this.translate.instant('PRODUCT'),
          isMultiSelect: true,
          options: this.getProductOptions(ActivityType.Order)
        }
        this.accountTimelineFilterFormView.push(productsViewData);
      }
      if (this.authenticationService.hasFeatureAction(FeatureActionsMap.PHONECALL_ACTIVITY)) {
        filterFormViewMain.options.push({ id: 'phoneCall-main', group: ActivityType.PhoneCall, isSelected: false, displayName: this.translate.instant('PHONE_CALLS') });
        const viewData = {
          id: 'phoneCall-status',
          parentId: ActivityType.PhoneCall,
          displayName: this.translate.instant('PHONE_CALLS') + ' - ' + this.translate.instant('STATUS'),
          isMultiSelect: true,
          options: [{
            group: this.translate.instant('STATUS'),
            id: ActivityType.PhoneCall + '_' + this.translate.instant("COMPLETED"),
            displayName: this.translate.instant("COMPLETED"),
            isSelected: false,
          }, {
            group: this.translate.instant('STATUS'),
            id: ActivityType.PhoneCall + '_' + this.translate.instant("OPEN"),
            displayName: this.translate.instant("OPEN"),
            isSelected: false,
          }]
        }
        this.accountTimelineFilterFormView.push(viewData);
      }
      if (this.authenticationService.user.hasBulkProcedureLog || this.authenticationService.user.hasProcedureLog) {
        filterFormViewMain.options.push({ id: 'surgeryOrder-main', group: ActivityType.SurgeryOrder, isSelected: false, displayName: this.translate.instant('PROCEDURE_LOGS') });
        //Products
        const productsViewData = {
          id: 'surgeryOrder-product',
          parentId: ActivityType.SurgeryOrder,
          displayName: this.translate.instant('PROCEDURE_LOGS') + ' - ' + this.translate.instant('PRODUCT'),
          isMultiSelect: true,
          options: this.getProductOptions(ActivityType.SurgeryOrder)
        }
        this.accountTimelineFilterFormView.push(productsViewData);
      }
      if (this.authenticationService.hasFeatureAction(FeatureActionsMap.ACCOUNT_SURVEY))
        filterFormViewMain.options.push({ id: 'customerSurvey-main', group: ActivityType.CustomerSurvey, isSelected: false, displayName: this.translate.instant('SURVEY') });
    }
  }

  private getProductOptions(activityType: ActivityType) {
    const productOptions = [];
    this.brandsService.brands.forEach(brand => {
      productOptions.push({
        group: this.translate.instant('PRODUCT'),
        id: activityType + '_' + brand.ID,
        displayName: brand.name,
        isSelected: false,
      });
    });
    return productOptions;
  }

  initList() {
    this._allTimelineActivities = [];
    this._filteredTimelineActivities = [];
    // Reset pagination when initializing list (only for timeline view)
    if (this.from === 'timeline') {
      this.page = 1;
    }

    if (isEmpty(this.accountOfflineService?.selected)) {
      this._initTimelineItemsViewData();
      return;
    }

    this._getTimelineActivities();
    const sectionConfig = {
      'quotes-section': {
        // filterInit: this._initQuoteListFilterOptions,
        searchFilterInit: this._initFilterFormViewsForQuoteSection
      },
      'salesorder-section': {
        // filterInit: this._initSalesOrderFilterOptions,
        searchFilterInit: this._initFilterFormViewsForSalesOrders
      },
      'asset-management-section': {
        // filterInit: this._initAssetManagementFilterOptions,
        searchFilterInit: this._initFilterFormViewsForAssetManagement
      },
      'accountPlan-section':{
        // filterInit: this._initFilterOptions,
        searchFilterInit: this.initFilterFormViewsForAccountPlan
      },
      default: {
        // filterInit: this._initFilterOptions,
        searchFilterInit: this._initFilterFormViews
      }
    };

    const config = sectionConfig[this.from] || sectionConfig.default;
    // if (this.filterEnabled) {
    //   config.filterInit.call(this);
    // }
    if (this.searchFilterEnabled) {
      config.searchFilterInit.call(this);
    }


    if (this.accountOfflineService.isOpenedSurveyTimelineFromAccountProfile && !isEmpty(this.filterOptions)) {
      this.filterOptions.map(option => {
        option.isSelected = option.value == ActivityType.CustomerSurvey;
      });
      this.accountTimelineFilter = ActivityType.CustomerSurvey;
    }

    this.onTimelineTypeChange(this.accountTimelineFilter);

    //Filter pre-selection based on survey, filter menu modal is not implemented yet
    let refDataFilterMenuList = this.searchConfigService.timelineFilterMenuList;

    if (isEmpty(this.accountOfflineService.selectedActivitiesFromAccountFilter) || isEmpty(refDataFilterMenuList)) {
      this._initTimelineItemsViewData();
      return;
    }

    this.isTimelineFiltered = true;
    let targetTimelineActivities = cloneDeep(this._allTimelineActivities);
    let activities = [];
    let filterBadgeCount: number = 0;
    let isSelectedMeetingStatus: boolean = false;

    this.accountOfflineService.selectedActivitiesFromAccountFilter.forEach(mainFilterValue => {
      // Update the value of the selected Activity displayed in the timeline filter menu (RHS)
      const foundFilterActivityValue = refDataFilterMenuList.find(d => d.category === mainFilterValue);
      if (foundFilterActivityValue && !this.selectedFilters.some(d => d.category === mainFilterValue)) {
        this.selectedFilters.push(foundFilterActivityValue);
      }

      if (!isEmpty(this.accountTimelineFilterFormView)) {
        this.accountTimelineFilterFormView.forEach(d => {
          if (d.id === "filter-main" && !isEmpty(d.options)) {
            const option = d.options.find(o => o.group === mainFilterValue);
            if (option) {
              option.isSelected = true;
              filterBadgeCount++;
            }
          }
        });
      }

      // Update Filtered activities
      targetTimelineActivities.forEach(activity => {
        if (mainFilterValue === ActivityType.LiveMeet && activity.type === ActivityType.Appointment && activity.location === "LiveMeet") {
          activities.push(activity);
        } else if (mainFilterValue === ActivityType.Appointment && activity.type === ActivityType.Appointment && activity.location !== "LiveMeet") {
          // Check Meeting activity option value
          if (!isEmpty(this.accountOfflineService.selectedSubOptionsFromAccountFilter.meetingStatus)) {
            if (this.accountOfflineService.selectedSubOptionsFromAccountFilter.meetingStatus.includes(activity.status)) {
              // Update the value of the selected Option displayed in the timeline filter menu (RHS)
              const formattedOptionId = `${ActivityType.Appointment}_${activity.statusString}`;
              const foundFilterOptionValue = refDataFilterMenuList.find(d => d.parentId === mainFilterValue && d.id === formattedOptionId);
              if (foundFilterOptionValue && !this.selectedFilters.some(d => d.id === formattedOptionId)) {
                this.selectedFilters.push(foundFilterOptionValue);
              }
              if (!isEmpty(this.accountTimelineFilterFormView)) {
                this.accountTimelineFilterFormView.forEach(d => {
                  if (d.id === "appointment-status" && !isEmpty(d.options)) {
                    const option = d.options.find(o => o.id === formattedOptionId);
                    if (option) {
                      option.isSelected = true;
                      isSelectedMeetingStatus = true;
                    }
                  }
                });
              }
              activities.push(activity);
            }
          } else {
            activities.push(activity);
          }
        } else if (mainFilterValue === activity.type) {
          activities.push(activity);
        }
      });
    });
    //Update filter badge count from selected option
    if (isSelectedMeetingStatus) {
      const idx = this.accountTimelineFilterFormView.findIndex(d => d.id === "appointment-status");
      if (idx > -1) {
        filterBadgeCount += this.accountTimelineFilterFormView[idx].options.reduce((count, o) => count + (o.isSelected ? 1 : 0), 0);
      }
    }

    targetTimelineActivities = activities;
    this.filterButtonBadgeCount = filterBadgeCount;
    /******* grouped filtered activities *******/
    this._allTimelineActivities = activities;
    this._filteredTimelineActivities = activities;
    this._initTimelineItemsViewData();
  }

  public async openSubmittedSurveyDetails(survey: any) {
    if (this.device.isOffline) {
      this.notifiationService.notify(this.translate.instant("SURVEY_ACTIVITIES_NOTIFY_ONLINE"), "contact-timeline", "top", ToastStyle.DANGER, 3000, true);
      return;
    }
    //check selected survey tempalte id
    const filteredSurveyList = this.customerAssessService.surveytemplates.filter(template => template.indskr_type == TemplateType.SURVEY);
    const templateId: string = survey['templateId'];
    const foundTemplate: AssessmentTemplate = filteredSurveyList.find(survey => survey.indskr_assessmenttemplateid == templateId);
    this.customerSurveyService.setCurrentSurvey(foundTemplate);
    try {
      await this.navService.pushChildNavPageWithPageTracking(PageName.SurveyDetailsComponent, PageName.SurveyDetailsComponent, PageName.AccountDetailsComponent, { appointmentId: '', from: PageName.AccountDetailsComponent }).then(async () => {
        this.navService.setChildNavRightPaneView(true);
        this.uiService.dismissLoader();
        this.customerSurveyService.selectedAccountForSurvey = this.accountOfflineService.selected;
        try {
          //map survey based on survey responses
          this.uiService.displayLoader();
          const responseData = await this.customerAssessService.fetchContactAccountSurveyForDetails(survey, SurveyCategory.ACCOUNT);
          if (!isEmpty(responseData)) {
            const formattedSurvey: CustomerAssessment = await this.customerAssessService.mapCustomerSurveyRespForTimeline(responseData);
            if (!isEmpty(formattedSurvey)) {
              this.customerSurveyService.setSurveyPreviewMode(true, formattedSurvey, 'timeline');
            }
          }
          this.uiService.dismissLoader();
        } catch (error) {
          console.error("Error open survey details from surve-timeline ", error);
          this.uiService.dismissLoader();
        }
      });
    } catch (error) {
      console.error("failed to open survey ", error);
      this.uiService.dismissLoader();
    }

  }

  private _initFilterFormViewsForOpportunities() {
    if(!isEmpty(this.selectedFilters)) return ;
    this.accountTimelineFilterFormView = [];
    if (!isEmpty(this._allTimelineActivities)) {
      let filterFormViewMain: IndFilterMenuModalDataModel = {
        id: 'filter-main',
        displayName: this.translate.instant('STATUS'),
        isMultiSelect: true,
        isExpanded: true,
        options: [],
      }
      this.accountTimelineFilterFormView.push(filterFormViewMain);
      filterFormViewMain.options.push({ id: 'status-open', group: 'Open', isSelected: false, displayName: this.translate.instant('OPEN') });
      filterFormViewMain.options.push({ id: 'status-won', group: 'Won', isSelected: false, displayName: this.translate.instant('WON') });
      filterFormViewMain.options.push({ id: 'status-lost', group: 'Lost', isSelected: false, displayName: this.translate.instant('OPPORTUNITY_STATE_LOST') });
    }
  }

  private async _applyAdvancedFilterOnOpportunities() {
    if (!isEmpty(this.accountTimelineFilterFormView)) {
      this.filterButtonBadgeCount = 0;
      this._filteredTimelineActivities = [];

      this.filterButtonBadgeCount = this.selectedFilters.length;
      if (!isEmpty(this.selectedDate) && (!isEmpty(this.selectedDate.startDate) || !isEmpty(this.selectedDate.endDate))) {
        this.filterButtonBadgeCount = this.filterButtonBadgeCount + 1;
      }
      let selectedActivitiesFromMainFilter = [];
      this.selectedFilters.forEach(item => {
        if (item.type == 'filter-main') {
          selectedActivitiesFromMainFilter.push(item.category);
        }
      });
      let targetActivities = cloneDeep(this._allTimelineActivities);
      if (!isEmpty(selectedActivitiesFromMainFilter)) {
        let filteredTimelineOpportunities = [];
        selectedActivitiesFromMainFilter.forEach(mainFilterValue => {
          targetActivities.forEach(filterOpp => {
            if (filterOpp.status == mainFilterValue) {
              filteredTimelineOpportunities.push(filterOpp);
            }
          });
        });
        targetActivities = filteredTimelineOpportunities;
      }
      /******* grouped filtered opportunities *******/

      this._filteredTimelineActivities = targetActivities;
      this._initTimelineItemsViewData();
      this._initSectionHeadersView();
    } else {
      this._filteredTimelineActivities = this._allTimelineActivities;
      this._initTimelineItemsViewData();
      this._initSectionHeadersView();
    }
  }

  private _initFilterFormViewsForSalesOrders() {
    this.accountTimelineFilterFormView = [];
    if (!isEmpty(this._allTimelineActivities)) {
      let salesOrderFilterFormViewStatus: IndFilterMenuModalDataModel = {
        id: 'filter-main-status',
        displayName: this.translate.instant('STATUS'),
        isMultiSelect: true,
        isExpanded: false,
        options: [],
      };

      let salesOrderFilterFormViewProduct: IndFilterMenuModalDataModel = {
        id: 'filter-main-product',
        displayName: this.translate.instant('PRODUCTS'),
        isMultiSelect: true,
        isExpanded: false,
        options: [],
      };

      this.accountTimelineFilterFormView.push(salesOrderFilterFormViewStatus);
      this.accountTimelineFilterFormView.push(salesOrderFilterFormViewProduct);

      let salesOrderActs = this._allTimelineActivities.filter(activity => activity.type == ActivityType.Order);
      salesOrderActs.forEach((act) => {
        // Status
        if (act.status) {
          const statusOptions = [
            { id: 'status-draft', group: 'Draft', isSelected: false, displayName: this.translate.instant('OM_DRAFT') },
            { id: 'status-for-review', group: 'For Review', isSelected: false, displayName: this.translate.instant('FOR_REVIEW') },
            { id: 'status-approved', group: 'Approved', isSelected: false, displayName: this.translate.instant('APPROVED') },
            { id: 'status-fulfilled', group: 'Fulfilled', isSelected: false, displayName: this.translate.instant('OM_FULFILLED') },
            { id: 'status-canceled', group: 'Canceled', isSelected: false, displayName: this.translate.instant('CANCELLED') }
          ];

          statusOptions.forEach(statusOption => {
            let option = {
              group: this.translate.instant('STATUS'),
              id: ActivityType.Order + '_' + statusOption.id,
              displayName: statusOption.displayName,
              isSelected: false,
            };
            let optionIdx = salesOrderFilterFormViewStatus.options.findIndex((opt) => opt.id == ActivityType.Order + '_' + statusOption.id);
            if (optionIdx < 0) salesOrderFilterFormViewStatus.options.push(option);
          });
        }

        // If Product exists
        if (act.products && act.products.length > 0) {
          act.products.forEach(product => {
            let option = {
              group: this.translate.instant('PRODUCTS'),
              id: ActivityType.Order + '_' + product.productId,
              displayName: product.productName,
              isSelected: false,
            };
            let optionIdx = salesOrderFilterFormViewProduct.options.findIndex((opt) => opt.id == ActivityType.Order + '_' + product.productId);
            if (optionIdx < 0) salesOrderFilterFormViewProduct.options.push(option);
          });
        }
      });
    }
  }

  private _initFilterFormViewsForProcedureContract() {
    if(!isEmpty(this.selectedFilters)) return ;
    this.accountTimelineFilterFormView = [];
    if (!isEmpty(this._allTimelineActivities)) {
      let FilterFormViewStatus: IndFilterMenuModalDataModel = {
        id: 'filter-main-status',
        displayName: this.translate.instant('STATUS'),
        isMultiSelect: true,
        isExpanded: false,
        options: [],
      };

      let FilterFormViewProduct: IndFilterMenuModalDataModel = {
        id: 'filter-main-product',
        displayName: this.translate.instant('PRODUCTS'),
        isMultiSelect: true,
        isExpanded: false,
        options: [],
      };

      let FilterFormViewContractType: IndFilterMenuModalDataModel = {
        id: 'filter-main-contractType',
        displayName: this.translate.instant('CONTRACT_TYPE'),
        isMultiSelect: true,
        isExpanded: false,
        options: [],
      };

      this.accountTimelineFilterFormView.push(FilterFormViewStatus);
      this.accountTimelineFilterFormView.push(FilterFormViewProduct);
      this.accountTimelineFilterFormView.push(FilterFormViewContractType);
      const statusOptions = this._allTimelineActivities.reduce((accumulator, procedureContract) => {
        if (!accumulator.some(item => item.text === procedureContract.statusString)) {
          accumulator.push({ text: procedureContract.statusString, value: procedureContract.statuscode });
        } return accumulator;
      }, []);
      statusOptions.forEach(status => {
        const statusString = this.procedureContractService.getStatusString(status.value) ?? status.text;
        let option = {
          group: this.translate.instant('STATUS'),
          id: 'contract-status' + '_' + status.value,
          displayName: statusString,
          isSelected: false,
        }
        let optionIdx = FilterFormViewStatus.options.findIndex((opt) => opt.id == 'contract-status' + '_' + status.value,);
        if (optionIdx < 0) FilterFormViewStatus.options.push(option);
      })

      let procedureContractproduct = this.getContractsForFilterPopover(this._allTimelineActivities)
      if (!isEmpty(procedureContractproduct)) {
        procedureContractproduct.forEach((product) => {
          let option = {
            group: this.translate.instant('PRODUCTS'),
            id: 'contract-product' + '_' + product.value,
            displayName: product.text,
            isSelected: false,
          };
          let optionIdx = FilterFormViewProduct.options.findIndex((opt) => opt.id == 'contract-product' + '_' + product.value);
          if (optionIdx < 0) FilterFormViewProduct.options.push(option);

        });
      }
      // contract type
      let contracttype = this.procedureContractService.getContractTypeFilterOptions(this._allTimelineActivities);
      if (!isEmpty(contracttype)) {
        contracttype.forEach((contracttype) => {
          let option = {
            group: this.translate.instant('CONTRACT_TYPE'),
            id: 'contract-type' + '_' + contracttype.value,
            displayName: contracttype.text,
            isSelected: false,
          };
          let optionIdx = FilterFormViewContractType.options.findIndex((opt) => opt.id == 'contract-type' + '_' + contracttype.value);
          if (optionIdx < 0) FilterFormViewContractType.options.push(option);

        });
      }
    }
  }

  private _applyAdvancedFilterOnProductContract() {
    if (isEmpty(this.accountTimelineFilterFormView)) {
      this._filteredTimelineActivities = this._allTimelineActivities;
      this._initTimelineItemsViewData();
      this._initSectionHeadersView();
      return;
    }

    this.filterButtonBadgeCount = this.selectedFilters.length;
    if (!isEmpty(this.selectedDate) && (!isEmpty(this.selectedDate.startDate) || !isEmpty(this.selectedDate.endDate))) {
      this.filterButtonBadgeCount += 1;
    }

    let targetActivities = Object.assign([], this._allTimelineActivities);

    // Collect all selected statuses
    const selectedStatuses = this.selectedFilters
      .filter(filter => filter.category === 'Status')
      .map(filter => filter.value);

    // Collect all selected products
    const selectedProducts = this.selectedFilters
      .filter(filter => filter.category === 'Products')
      .map(filter => filter.value);

    const selectedContractTypes = this.selectedFilters
      .filter(filter => filter.category === 'Contract Type')
      .map(filter => filter.value);

    // Apply status and product filters
    if (selectedStatuses.length > 0 || selectedProducts.length > 0 || selectedContractTypes.length > 0) {
      targetActivities = targetActivities.filter(procedureContract => {
        const statusMatch = selectedStatuses.length > 0 ? selectedStatuses.includes(procedureContract.statusString) : true;
        const productMatch = selectedProducts.length > 0 ? selectedProducts.includes(procedureContract.productName) : true;
        const contractTypeMatch = selectedContractTypes.length > 0 ? selectedContractTypes.includes(procedureContract.contractTypeString) : true;
        return statusMatch && productMatch && contractTypeMatch;
      });
    }

    this._filteredTimelineActivities = targetActivities;
    this._initTimelineItemsViewData();
    this._initSectionHeadersView();
  }

  /** Initializing the filter options for the search bar filter in the quotes section of omnichannel tab**/
  private _initFilterFormViewsForQuoteSection() {
    if(!isEmpty(this.selectedFilters)) return ;
    this.accountTimelineFilterFormView = [];
    if (!isEmpty(this._allTimelineActivities)) {
      let filterFormViewStatus: IndFilterMenuModalDataModel = {
        id: 'filter-main-status',
        displayName: this.translate.instant('STATUS'),
        isMultiSelect: true,
        isExpanded: false,
        options: [],
      };

      let filterFormViewProduct: IndFilterMenuModalDataModel = {
        id: 'filter-main-product',
        displayName: this.translate.instant('PRODUCTS'),
        isMultiSelect: true,
        isExpanded: false,
        options: [],
      };

      // Extract unique status and product options
      const statusOptionsSet = new Set<string>();
      const productOptionsSet = new Set<string>();

      this._allTimelineActivities.forEach(quote => {
        // Handle status options
        const statusText = this.quotesService.getFormattedStatus(quote.statuscode);
        if (!statusOptionsSet.has(statusText)) {
          statusOptionsSet.add(statusText);
          filterFormViewStatus.options.push({
            group: this.translate.instant('STATUS'),
            id: ActivityType.Other + '_' + quote.statuscode,
            displayName: statusText,
            isSelected: false,
          });
        }
        // Handle product options
        if (quote.products && quote.products.length > 0) {
          quote.products.forEach(product => {
            if (!productOptionsSet.has(product.id)) {
              productOptionsSet.add(product.id);
              filterFormViewProduct.options.push({
                group: this.translate.instant('PRODUCTS'),
                id: product.id,
                displayName: product.name,
                isSelected: false,
              });
            }
          });
        }
      });
      this.accountTimelineFilterFormView.push(filterFormViewStatus);
      this.accountTimelineFilterFormView.push(filterFormViewProduct);
    }
  }

  /** Function to filter the assets when the apply button in the search bar filter in omnichannel tab is clicked**/
  private _applyAdvancedFilterOnAssetManagement() {
    if (isEmpty(this.accountTimelineFilterFormView)) {
      this._filteredTimelineActivities = this._allTimelineActivities;
      this._initTimelineItemsViewData();
      this._initSectionHeadersView();
      return;
    }

    this.filterButtonBadgeCount = this.selectedFilters.length;
    if (!isEmpty(this.selectedDate?.startDate) || !isEmpty(this.selectedDate?.endDate)) {
      this.filterButtonBadgeCount += 1;
    }

    const selectedStatuses = new Set(this.selectedFilters.filter(filter => filter.category === 'Status').map(filter => filter.value));
    const selectedProducts = new Set(this.selectedFilters.filter(filter => filter.category === 'Products').map(filter => filter.value));
    const selectedCategories = new Set(this.selectedFilters.filter(filter => filter.category === 'Category').map(filter => filter.value));

    this._filteredTimelineActivities = this._allTimelineActivities.filter(procedureContract => {
      const statusMatch = !selectedStatuses.size || selectedStatuses.has(procedureContract.indskr_assetstatus);
      const productMatch = !selectedProducts.size || selectedProducts.has(procedureContract.productName);
      const contractTypeMatch = !selectedCategories.size || selectedCategories.has(procedureContract.categoryName);
      return statusMatch && productMatch && contractTypeMatch;
    });

    this._initTimelineItemsViewData();
    this._initSectionHeadersView();
  }

  /** Initializing the filter options for the search bar filter in the assets section of omnichannel tab**/

  private _initFilterFormViewsForAssetManagement() {
    if (isEmpty(this._allTimelineActivities)) {
      this.accountTimelineFilterFormView = [];
      return;
    }
    const createFilterView = (id: string, displayName: string): IndFilterMenuModalDataModel => ({
      id,
      displayName: this.translate.instant(displayName),
      isMultiSelect: true,
      isExpanded: false,
      options: [],
    });
    const filterViews = {
      status: createFilterView('filter-main-status', 'STATUS'),
      product: createFilterView('filter-main-product', 'PRODUCTS'),
      category: createFilterView('filter-main-category', 'CATEGORY'),
    };
    const optionSets = {
      status: new Set<string>(),
      product: new Set<string>(),
      category: new Set<string>(),
    };
    this._allTimelineActivities.forEach(asset => {
      // Status
      if (asset.indskr_assetstatus && !optionSets.status.has(asset.indskr_assetstatus)) {
        optionSets.status.add(asset.indskr_assetstatus);
        filterViews.status.options.push({
          group: this.translate.instant('STATUS'),
          id: `status_${asset.indskr_assetstatus_value}`,
          displayName: asset.indskr_assetstatus,
          isSelected: false,
        });
      }

      // Product
      if (asset.productName && !optionSets.product.has(asset.productName)) {
        optionSets.product.add(asset.productName);
        filterViews.product.options.push({
          group: this.translate.instant('PRODUCTS'),
          id: asset.msdyn_product,
          displayName: asset.productName,
          isSelected: false,
        });
      }

      // Category
      if (asset.categoryName && !optionSets.category.has(asset.categoryName)) {
        optionSets.category.add(asset.categoryName);
        filterViews.category.options.push({
          group: this.translate.instant('CATEGORY'),
          id: `category_${asset.indskr_assetscategory}`,
          displayName: asset.categoryName,
          isSelected: false,
        });
      }
    });

    this.accountTimelineFilterFormView = Object.values(filterViews);
  }

  private initFilterFormViewsForAccountPlan() {
    this.accountTimelineFilterFormView = [];
    if (!isEmpty(this._allTimelineActivities)) {
      let FilterFormViewProduct: IndFilterMenuModalDataModel = {
        id: 'filter-main-product',
        displayName: this.translate.instant('PRODUCTS'),
        isMultiSelect: true,
        isExpanded: false,
        options: [],
      };

      let FilterFormViewCustomers: IndFilterMenuModalDataModel = {
        id: 'filter-main-contractType',
        displayName: this.translate.instant('CUSTOMERS'),
        isMultiSelect: true,
        isExpanded: false,
        options: [],
      };

      let filterFormViewMain: IndFilterMenuModalDataModel = {
        id: 'filter-main',
        displayName: this.translate.instant('Start Period & End Period'),
        isMultiSelect: true,
        isExpanded: true,
        options: [],
      }
      this.accountTimelineFilterFormView.push(FilterFormViewCustomers);
      this.accountTimelineFilterFormView.push(FilterFormViewProduct);
      this.accountTimelineFilterFormView.push(filterFormViewMain);

      let procedureContractproduct = this.getProductForAccountPlan(this._allTimelineActivities)
      if (!isEmpty(procedureContractproduct)) {
        procedureContractproduct.forEach((product) => {
          let option = {
            group: this.translate.instant('PRODUCTS'),
            id: 'accountPlan-product' + '_' + product.value,
            displayName: product.text,
            isSelected: false,
          };
          let optionIdx = FilterFormViewProduct.options.findIndex((opt) => opt.id == 'accountPlan-product' + '_' + product.value);
          if (optionIdx < 0) FilterFormViewProduct.options.push(option);

        });
      }

      let customers = this.getCustomerFilterOptions(this._allTimelineActivities);
      if (!isEmpty(customers)) {
        customers.forEach((customer) => {
          let option = {
            group: this.translate.instant('CUSTOMERS'),
            id: 'customers' + '_' + customer.value,
            displayName: customer.text,
            isSelected: false,
          };
          let optionIdx = FilterFormViewCustomers.options.findIndex((opt) => opt.id == 'customers' + '_' + customer.value);
          if (optionIdx < 0) FilterFormViewCustomers.options.push(option);

        });
      }

    }
  }

  getProductForAccountPlan(accountPlans): { text: string, value: string }[] {
    let Products = [];
    for (let accountPlan of accountPlans) {
      let products = accountPlan.products.filter(p => p.productID);
      for (let product of products) {
        if (product.productName && product.productID) {
          Products.push({ text: product.productName, value: product.productID })
        }
      }
    }
    return uniqBy(Products, 'text');
  }

  public getCustomerFilterOptions(accountPlans): { text: string, value: string }[] {
    const Customers = [];
    for (let accountPlan of accountPlans) {
      let contacts = accountPlan.contactAccountAffs
      for (let contact of contacts) {
        if (contact.contactFullName && contact.contactId) {
          Customers.push({ text: contact.contactFullName, value: contact.contactId })
        }
      }
    }
    return uniqBy(Customers, 'text');
  }

  private _applyAdvancedFilterOnAccountPlan() {
    if (isEmpty(this.accountTimelineFilterFormView) || isEmpty(this.selectedDate)) {
      this._filteredTimelineActivities = this._allTimelineActivities;
      this.filterButtonBadgeCount = 0;
      this._initTimelineItemsViewData();
      this._initSectionHeadersView();
      return;
    }
    this.filterButtonBadgeCount = this.selectedFilters.length;
    if (!isEmpty(this.selectedDate) && (!isEmpty(this.selectedDate.startDate) || !isEmpty(this.selectedDate.endDate))) {
      this.filterButtonBadgeCount += 1;
    }
    let isFilteredByDate: boolean = !isEmpty(this.selectedDate) && !isEmpty(this.selectedDate.startDate) && !isEmpty(this.selectedDate.endDate) || false;
    let targetActivities = Object.assign([], this._allTimelineActivities);

    // Collect all selected Customers
    const selectedCustomers = this.selectedFilters
      .filter(filter => filter.category === 'Customers')
      .map(filter => filter.value);

    // Collect all selected products
    const selectedProducts = this.selectedFilters
      .filter(filter => filter.category === 'Products')
      .map(filter => filter.value);


    // Apply Customers and product filters
    if (selectedCustomers.length > 0 || selectedProducts.length > 0) {
      targetActivities = targetActivities.filter(accountPlan => {
        const CustomerMatch = selectedCustomers.length > 0 ? accountPlan.contactAccountAffs.some(contact => selectedCustomers.includes(contact.contactFullName)) : true;
        const productMatch = selectedProducts.length > 0 ? accountPlan.products.some(prod => selectedProducts.includes(prod.productName)) : true;
        return CustomerMatch && productMatch;
      });
    }
    if (isFilteredByDate) {
      targetActivities = this._filterAccountPlanByDate(targetActivities, this.selectedDate);
    }



    this._filteredTimelineActivities = targetActivities;
    this._initTimelineItemsViewData();
    this._initSectionHeadersView();
  }

  private _filterAccountPlanByDate(targetActivities: any, selectedDate: any) {
    const startDate: Date = new Date(selectedDate.startDate);
    const endDate: Date = new Date(selectedDate.endDate);
    let filteredTimelineActivities = [];
    targetActivities.forEach(accountPlan => {
      if (accountPlan.startDate && accountPlan.endDate) {
        const accountPlanStartDate = this.dateTimeFormatsService.getStartOfDay(new Date(parseInt(accountPlan.startDate)));
        const accountPlanEndDate = this.dateTimeFormatsService.getStartOfDay(new Date(parseInt(accountPlan.endDate)));
        if (accountPlanStartDate >= startDate && accountPlanEndDate <= endDate) {
          filteredTimelineActivities.push(accountPlan);
        }
      }
    })
    return filteredTimelineActivities;
  }

  openDyanmicURl(opportunity) {
    this.opportunityService.openOpportunityDynamicLink(opportunity)
  }

  async loadActivitiesByPage(page: number): Promise<[boolean, any[]]> {
    if (this.device.isOffline) {
      this.notifiationService.notify(this.translate.instant('ACCOUNT_TIMELINE_NO_INTERNET'), 'Account Timeline', 'top', ToastStyle.DANGER);
      return [false, []];
    }
    const data = await this.accountDataService.getAccountTimelineInfoPaginated(this.accountOfflineService.selected, page, this.pageSize);
    let hasAnyData = false;
    let items: any[] = [];
    if (data) {
      data.forEach(d => {
        if (d.status === 'fulfilled' && d.value && d.value.length > 0) {
          items = [...items, ...d.value];
          hasAnyData = true;
        }
      })
    }
    items = uniqBy(items, 'ID');
    items = this.filterAndSortTimelineItems(items, false);
    return [hasAnyData, items];
  }

  async loadMore(event: Event) {
    // Only apply infinite scroll logic for timeline view
    if (this.from !== 'timeline') {
      (event as InfiniteScrollCustomEvent).target.complete();
      return;
    }
    if (this.device.isOffline) {
      this.notifiationService.notify(this.translate.instant('ACCOUNT_TIMELINE_NO_INTERNET'), 'Account Timeline', 'top', ToastStyle.DANGER);
      return;
    }
    if (this.isActivitiesLoading) {
      (event as InfiniteScrollCustomEvent).target.complete();
      return;
    }
    this.isActivitiesLoading = true;
    this.page++;
    const [hasData, items] = await this.loadActivitiesByPage(this.page);
    
    if (hasData && items.length > 0) {
      // Convert items to timeline view format
      const newTimelineItems: TimeLineItem[] = this._getTimelineItemViewForActivities(items);
      
      // Add to filtered activities
      this._filteredTimelineActivities.push(...items);
      this._allTimelineActivities = this._filteredTimelineActivities;
      
      // Add to view activities
      this._filteredTimelineViewActivities.push(...newTimelineItems);
      
      // Sort all arrays to maintain chronological order
      this._filteredTimelineActivities = this.filterAndSortTimelineItems(this._filteredTimelineActivities, false);
      this._allTimelineActivities = this.filterAndSortTimelineItems(this._allTimelineActivities, false);
      this._filteredTimelineViewActivities = this.filterAndSortTimelineItems(this._filteredTimelineViewActivities, true);
      
      // Update the displayed timeline items
      this.timelineItems = [...this._filteredTimelineViewActivities];
      this.refreshOrderCaptureStatusOptions();
      this._applyAdvancedFilterOnTimelineActivities();
      
      
      
      // Apply search filter if active
      this.timelineItems = this._filterTimelineItemsBySearch(this.timelineItems);
    }
    
    (event as InfiniteScrollCustomEvent).target.complete();
    
    if (!hasData) {
      (event as InfiniteScrollCustomEvent).target.disabled = true;
    } else {
      // Ensure infinite scroll is enabled when there's more data
      if (this.infiniteScroll) {
        this.infiniteScroll.disabled = false;
      }
    }
    this.isActivitiesLoading = false;
  }

}
